<script setup>
// =============================================================================
// FangateSettings.vue
//
// Inline settings views rendered in Col 3 (Chat Area) when the user clicks
// a Settings sub-item in the Fangate sidebar.
//
// View IDs:
//   - 'settings-inboxes' : Chat-Accounts (table + add/edit/delete)
//   - 'settings-bots'    : Bots & AI placeholder
//   - 'settings-account' : current user profile
// =============================================================================

import { ref, computed, reactive, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import FangateAPI from 'dashboard/api/fangate';
import authApi from 'dashboard/api/auth';
import FangateAddAccountModal from './components/FangateAddAccountModal.vue';
import { useChatGptState } from 'dashboard/composables/useChatGptState';
import { useAlert } from 'dashboard/composables';
import { useFangateConfirm } from 'dashboard/composables/useFangateConfirm';

const props = defineProps({
  /** Which settings panel to show */
  view:           { type: String, default: 'settings-inboxes' },
  /** Inboxes (passed from Layout) */
  inboxes:        { type: Array, default: () => [] },
  /** Agent-assignment map { [inboxId]: agentId[] } (local) */
  inboxAgentMap:  { type: Object, default: () => ({}) },
  /** Full agents list to resolve assignment ids → names */
  agents:         { type: Array, default: () => [] },
  /** Labels from Vuex labels store — drives the Labels CRUD table */
  labels:         { type: Array, default: () => [] },
  /** Mobile (≤768px) — render an in-header back button to the settings menu */
  isMobile:       { type: Boolean, default: false },
});

const emit = defineEmits(['open-agent-picker', 'back']);

const store = useStore();
const route = useRoute();
const { confirm } = useFangateConfirm();
const accountId = computed(() => route.params.accountId);
const currentUser = computed(() => store.getters.getCurrentUser ?? {});

// ============================================================================
// My Account page — profile + security + plan + support + logout (Task #209)
// Talks to auth.js#profileUpdate (multipart for avatar) and
// profilePasswordUpdate (Devise current-password gated). Plan is a static
// "FREE" badge until tiers ship.
// ============================================================================
const avatarFileRef = ref(null);
const acctForm = reactive({
  name: '',
});
const acctState = reactive({
  avatarFile:        null,   // File object selected for upload
  avatarPreview:     null,   // dataURL preview before save
  profileSaving:     false,
  profileError:      '',
  profileSavedFlash: false,
});
const acctPw = reactive({
  current:  '',
  next:     '',
  confirm:  '',
  saving:   false,
  error:    '',
  okFlash:  false,
  open:     false,   // Security card collapse — fields hidden until user opts in
});

/** Open the Security form. Reset stale state first so re-opens are clean. */
const openPwForm = () => {
  acctPw.current = '';
  acctPw.next    = '';
  acctPw.confirm = '';
  acctPw.error   = '';
  acctPw.okFlash = false;
  acctPw.open    = true;
};

/** Cancel out of the Security form — clear inputs and recollapse. */
const cancelPwForm = () => {
  acctPw.current = '';
  acctPw.next    = '';
  acctPw.confirm = '';
  acctPw.error   = '';
  acctPw.open    = false;
};

// Seed form from currentUser whenever the user lands on the My Account view,
// and again whenever the Vuex currentUser updates (e.g. after save).
watch(
  [() => props.view, currentUser],
  ([view, u]) => {
    if (view !== 'settings-account') return;
    acctForm.name          = u?.name || '';
    acctState.profileError = '';
  },
  { immediate: true }
);

const onAvatarFile = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;
  if (!/^image\//.test(file.type)) {
    acctState.profileError = 'Avatar must be an image.';
    return;
  }
  if (file.size > 5 * 1024 * 1024) {
    acctState.profileError = 'Avatar must be under 5 MB.';
    return;
  }
  acctState.avatarFile = file;
  // Inline data-URL preview so the round avatar updates before save.
  const reader = new FileReader();
  reader.onload = () => { acctState.avatarPreview = reader.result; };
  reader.readAsDataURL(file);
};

const removeAvatar = async () => {
  if (!(await confirm({ title: 'Remove avatar?', message: 'Remove your profile photo?', confirmLabel: 'Remove', danger: true }))) return;
  try {
    // Re-using profileUpdate with an empty avatar field tells Chatwoot's
    // ProfilesController#avatar to detach the attachment. We still send
    // displayName so the multipart payload is well-formed (Chatwoot's
    // auth.js sets it as form-field regardless); we just send the existing
    // value back unchanged.
    await authApi.profileUpdate({
      displayName: currentUser.value?.display_name,
      avatar: '',  // empty string clears it server-side
    });
    acctState.avatarFile    = null;
    acctState.avatarPreview = null;
    await store.dispatch('updateProfile', currentUser.value);
    acctState.profileSavedFlash = true;
    setTimeout(() => { acctState.profileSavedFlash = false; }, 2200);
  } catch (e) {
    acctState.profileError = e?.response?.data?.message || e?.message || 'Failed to remove avatar.';
  }
};

const saveProfile = async () => {
  acctState.profileError = '';
  if (!acctForm.name.trim()) { acctState.profileError = 'Name is required.'; return; }
  acctState.profileSaving = true;
  try {
    await authApi.profileUpdate({
      // displayName intentionally re-sent unchanged: Chatwoot's auth.js
      // multipart payload includes it as a required field. Sending the
      // current value back is a no-op server-side and keeps the request
      // schema happy without exposing a "Display name" field in our UI
      // (Telegram-side display always wins anyway — that's the agent's
      // real outbound identity in chat).
      displayName: currentUser.value?.display_name || '',
      avatar:      acctState.avatarFile,  // File object or null — auth.js handles either
      name:        acctForm.name.trim(),
    });
    // Refresh Vuex so currentUser reflects the saved state and avatars
    // across the shell update immediately.
    if (store.dispatch) {
      try { await store.dispatch('updateProfile', currentUser.value); } catch (_) { /* noop */ }
    }
    acctState.avatarFile        = null;
    acctState.avatarPreview     = null;
    acctState.profileSavedFlash = true;
    setTimeout(() => { acctState.profileSavedFlash = false; }, 2200);
  } catch (e) {
    acctState.profileError = e?.response?.data?.message || e?.message || 'Profile save failed.';
  } finally {
    acctState.profileSaving = false;
  }
};

const savePassword = async () => {
  acctPw.error = '';
  if (!acctPw.current) { acctPw.error = 'Enter your current password.'; return; }
  if ((acctPw.next || '').length < 8) { acctPw.error = 'New password must be at least 8 characters.'; return; }
  if (acctPw.next !== acctPw.confirm) { acctPw.error = 'New password and confirmation don\'t match.'; return; }
  acctPw.saving = true;
  try {
    await authApi.profilePasswordUpdate({
      currentPassword:      acctPw.current,
      password:             acctPw.next,
      passwordConfirmation: acctPw.confirm,
    });
    acctPw.current = ''; acctPw.next = ''; acctPw.confirm = '';
    acctPw.okFlash = true;
    // Collapse the form back to a single CTA once the update lands —
    // the green "✓ Password updated" flash above the CTA reassures the
    // user without keeping three empty password fields around.
    acctPw.open = false;
    setTimeout(() => { acctPw.okFlash = false; }, 2400);
  } catch (e) {
    acctPw.error = e?.response?.data?.message
                  || e?.response?.data?.error
                  || 'Password update failed — check that the current password is correct.';
  } finally {
    acctPw.saving = false;
  }
};

const confirmLogout = async () => {
  if (!(await confirm({ title: 'Sign out?', message: 'Sign out of Creator-Chat?', confirmLabel: 'Sign out' }))) return;
  try {
    await authApi.logout();
  } catch (_) { /* Devise logout might 204 — ignore */ }
  // Hard reload to /app/login so all Vuex + Pinia state clears.
  window.location.href = '/app/login';
};

// ----- Delete my account (Danger Zone) -------------------------------------
// Self-service account deletion. The backend re-checks the password against the
// DB and applies the one-agency cascade (last admin -> whole agency incl. agents;
// otherwise just leave). Irreversible.
const deleteAcct = reactive({ open: false, password: '', busy: false, error: '', otherAdmins: null, done: false });
const isAccountAdmin = computed(() => store.getters.getCurrentRole === 'administrator');

// 'agent' | 'co_admin' | 'last_admin' | 'unknown'
const deleteScenario = computed(() => {
  if (!isAccountAdmin.value) return 'agent';
  if (deleteAcct.otherAdmins == null) return 'unknown';
  return deleteAcct.otherAdmins > 0 ? 'co_admin' : 'last_admin';
});
const deleteTitle = computed(() => ({
  last_admin: 'This deletes your entire agency',
  co_admin: 'Only your account will be deleted',
  agent: 'This deletes your account',
  unknown: 'Delete your account',
}[deleteScenario.value]));
const deleteBody = computed(() => ({
  last_admin: 'You are the last admin of this agency. Deleting your account permanently removes the ENTIRE agency — all chat accounts, conversations and agents.',
  co_admin: 'Another admin remains, so only YOUR account is deleted — the agency and its data stay. The whole agency is deleted only when its last admin leaves.',
  agent: 'Your account will be permanently removed from this agency.',
  unknown: 'Your account will be permanently deleted.',
}[deleteScenario.value]));

const openDeleteAccount = async () => {
  Object.assign(deleteAcct, { open: true, password: '', error: '', otherAdmins: null, done: false });
  try {
    await store.dispatch('agents/get');
    const myId = currentUser.value?.id;
    deleteAcct.otherAdmins = (store.getters['agents/getAgents'] || [])
      .filter(a => a.role === 'administrator' && a.id !== myId).length;
  } catch (_) { deleteAcct.otherAdmins = null; }
};
const closeDeleteAccount = () => { deleteAcct.open = false; };

const confirmDeleteAccount = async () => {
  if (!deleteAcct.password) { deleteAcct.error = 'Please enter your password.'; return; }
  deleteAcct.busy = true;
  deleteAcct.error = '';
  try {
    // Use the CONFIGURED global axios (window.axios = createAxios) — it carries the
    // DeviseTokenAuth headers (access-token/client/uid). The raw `import axios` does
    // NOT, which made this request hit authenticate_user! → 401 before the password
    // check/cascade ever ran.
    await window.axios.delete('/api/v1/profile', { data: { profile: { current_password: deleteAcct.password } } });
    // Show a brief success confirmation, THEN drop the (now-invalid) session + redirect.
    deleteAcct.done = true;
    setTimeout(async () => {
      try { await authApi.logout(); } catch (_) { /* ignore — user already gone */ }
      window.location.href = '/app/login';
    }, 1800);
  } catch (e) {
    deleteAcct.error =
      e?.response?.data?.message || e?.response?.data?.error || 'Could not delete account — check your password and try again.';
    deleteAcct.busy = false;
  }
};

// ----- Helpers --------------------------------------------------------------
const getInitials = name => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
};

const platformOf = inbox => {
  const t = (inbox.channel_type || '').toLowerCase();
  if (t.includes('telegram')) return 'telegram';
  if (t.includes('whatsapp')) return 'whatsapp';
  if (t.includes('instagram')) return 'instagram';
  if (t.includes('facebook')) return 'facebook';
  if (t.includes('email'))    return 'email';
  if (t.includes('sms'))      return 'sms';
  return 'other';
};

const platformLabel = inbox => {
  const map = {
    telegram:  'Telegram',
    whatsapp:  'WhatsApp',
    instagram: 'Instagram',
    facebook:  'Facebook',
    email:     'Email',
    sms:       'SMS',
    other:     (inbox.channel_type || 'Inbox').replace('Channel::', ''),
  };
  return map[platformOf(inbox)];
};

// Chat-Accounts only contains real user accounts — bots live under Bots & AI.
// Kept as a helper for future use (e.g., when we add a bot/user flag to inboxes).
const typeOf = () => 'user';

// Handle string ("@username" or phone or email)
const handleOf = inbox => {
  if (inbox.bot_username) return '@' + inbox.bot_username;
  if (inbox.phone_number) return inbox.phone_number;
  if (inbox.email)        return inbox.email;
  return '—';
};

const isActive = inbox => inbox.enable_auto_assignment !== false;

const assignedAgents = inbox => {
  const ids = props.inboxAgentMap[inbox.id] || [];
  return ids
    .map(id => props.agents.find(a => a.id === id))
    .filter(Boolean)
    // Admins have access to every account anyway — don't list them as assigned
    // agents in the Chat-Accounts table.
    .filter(a => a.role !== 'administrator');
};

// ----- Actions menu state ---------------------------------------------------
const openMenuId = ref(null);
const toggleMenu = (e, inboxId) => {
  e.stopPropagation();
  openMenuId.value = openMenuId.value === inboxId ? null : inboxId;
};
const closeMenus = () => { openMenuId.value = null; accSortOpen.value = false; };
onMounted(()  => document.addEventListener('click', closeMenus));
onUnmounted(() => document.removeEventListener('click', closeMenus));

// ----- Chat-Accounts table sort --------------------------------------------
const ACC_SORT_OPTIONS = [
  { id: 'alpha',  label: 'Alphabetical' },
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
];
const accSort      = ref('alpha');
const accSortOpen  = ref(false);
const accSortLabel = computed(
  () => ACC_SORT_OPTIONS.find(o => o.id === accSort.value)?.label || 'Sort'
);
const setAccSort = id => { accSort.value = id; accSortOpen.value = false; };
// Newest/Oldest sort by the inbox id (auto-increment ⇒ reliable creation order,
// no date-format guessing); Alphabetical sorts by account name.
const sortedInboxes = computed(() => {
  const list = [...(props.inboxes || [])];
  if (accSort.value === 'alpha')  return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  if (accSort.value === 'newest') return list.sort((a, b) => Number(b.id) - Number(a.id));
  if (accSort.value === 'oldest') return list.sort((a, b) => Number(a.id) - Number(b.id));
  return list;
});

// ----- Fangate link status per inbox (used by the Chat-Accounts table) -----
// Fangate has no documented "linked integration" concept, so we define linked
// as "the stored token validates successfully against /api/products". Run a
// silent check for every inbox that has a stored token, then keep the result
// in `linkStatus` so the table can colour each row.
//
// States: 'unlinked' (no token) | 'checking' | 'linked' | 'rejected'
const linkStatus = reactive({});
// inboxId -> vault email (shown in the linked pill instead of the word "Linked")
const linkEmail = reactive({});
const webhookState = reactive({});   // inboxId -> 'not_connected' | 'connected' | 'errors' (Live-Sales webhook)

// ----- Account type per inbox (drives the Type column) ----------------------
// Stored server-side on channel_telegram.account_type (set at creation in the
// Add modal, editable in the Edit modal) and exposed as inbox.account_type by
// the inbox serializer. Reactive via Vuex — no localStorage, persists on Hetzner.
//   'bot'      → Bot
//   'personal' → Enduser (Telegram Premium)   [bot connected to a Premium acct]
//   'mtproto'  → Enduser                       [future: real user session]
const ACCOUNT_TYPE_LABELS = {
  bot:      { label: 'Bot',         icon: '🤖' },
  // Premium-Bot = a bot connected to a Premium user account via Telegram
  // Business — technically still a bot, just routed via the user's Premium
  // wrapper, so it shares the 🤖 icon. The label distinguishes it from a
  // standalone Bot for the Chat-Accounts overview.
  personal: { label: 'Premium-Bot', icon: '🤖' },
  mtproto:  { label: 'Enduser',     icon: '👤' },
};
const accountTypeMeta = inbox => {
  // Channel::TelegramUser inboxes are MTProto-Enduser by construction —
  // the model has no `account_type` column, so the bot/personal/mtproto string
  // doesn't apply. Detect via channel_type and short-circuit to the Enduser
  // label so the Type column doesn't fall back to "Bot".
  if (inbox?.channel_type === 'Channel::TelegramUser') return ACCOUNT_TYPE_LABELS.mtproto;
  return ACCOUNT_TYPE_LABELS[inbox?.account_type] || ACCOUNT_TYPE_LABELS.bot;
};

// ----- MTProto health (Phase C-3) ------------------------------------------
// Backend ships `health_status`, `health_reason`, `health_status_changed_at`,
// `health_blocked_until` on every TelegramUser inbox (see _inbox.json.jbuilder).
// We turn that into a colored pill in the Account cell + a prominent banner
// above the table whenever any account is not healthy.
//
// The pause countdown ticks down WITHOUT a server round-trip: a `now` ref
// auto-refreshes every 30s and the healthMeta() recomputes against it. The
// final crossover (flood_wait → healthy) lands when the watchdog or the next
// successful send updates health_status on the backend.
const LONG_FLOOD_WAIT_SECONDS = 24 * 60 * 60;
const now = ref(Math.floor(Date.now() / 1000));
let healthTickerId = null;
onMounted(() => { healthTickerId = setInterval(() => { now.value = Math.floor(Date.now() / 1000); }, 30 * 1000); });
onUnmounted(() => { if (healthTickerId) clearInterval(healthTickerId); });

// Days / hours / minutes / seconds — only the leading non-zero component and
// everything below it gets shown, so the live countdown for a long FloodWait
// reads as "2d 3h 45m 12s" and a short one as just "45m 12s" or "12s".
const formatRemaining = secs => {
  const total = Math.max(0, Math.floor(secs));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (d || h) parts.push(`${h}h`);
  if (d || h || m) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
};

// `null` when healthy → callers skip rendering the pill + banner.
// Otherwise: { severity: 'critical'|'high'|'medium'|'low', dotClass, label,
//   bannerText, recoveryUrl, recoveryLabel }.
const healthMeta = inbox => {
  if (!inbox) return null;
  const isBot = inbox.channel_type === 'Channel::Telegram';        // Bot / Business
  const isMtproto = inbox.channel_type === 'Channel::TelegramUser'; // Enduser
  if (!isBot && !isMtproto) return null;

  const status = inbox.health_status;
  // Both paths surface a green Healthy pill so the column is never empty —
  // empty looked broken in the wild.
  if (!status || status === 'healthy') return {
    severity: 'ok', dotClass: 'fs-pill--green', label: 'Healthy',
    bannerText: isBot ? 'Bot webhook registered with Telegram and delivering.' : 'Session connected and reachable.',
    recoveryUrl: null, recoveryLabel: null,
  };

  // ---- Bot / Business (Channel::Telegram) — #4 getWebhookInfo watchdog ----
  if (isBot) {
    if (status === 'unauthorized') return {
      severity: 'critical', dotClass: 'fs-pill--red', label: 'Bot token invalid',
      bannerText: 'Telegram rejected the bot token (getMe failed) — it was likely revoked in @BotFather. Edit this chat account and paste a fresh bot token.',
      recoveryUrl: 'https://t.me/botfather', recoveryLabel: 'Open @BotFather',
    };
    if (status === 'webhook_missing' || status === 'webhook_mismatch') return {
      severity: 'high', dotClass: 'fs-pill--orange', label: 'Disconnected',
      bannerText: "Telegram has no (or a different) webhook for this bot, so incoming messages won't arrive. Re-save the chat account to re-register the webhook.",
      recoveryUrl: null, recoveryLabel: null,
    };
    if (status === 'webhook_error') return {
      severity: 'high', dotClass: 'fs-pill--orange', label: 'Unstable',
      bannerText: `Telegram reported a recent delivery error to our webhook${inbox.health_reason ? ': ' + inbox.health_reason : ''}.`,
      recoveryUrl: null, recoveryLabel: null,
    };
    if (status === 'backlog') return {
      severity: 'medium', dotClass: 'fs-pill--yellow', label: 'Update backlog',
      bannerText: 'Telegram has a backlog of undelivered updates for this bot — messages may be delayed.',
      recoveryUrl: null, recoveryLabel: null,
    };
    return {
      severity: 'low', dotClass: 'fs-pill--orange', label: status,
      bannerText: `Unexpected health status: ${status}.`,
      recoveryUrl: null, recoveryLabel: null,
    };
  }

  // ---- MTProto (Channel::TelegramUser) — sidecar watchdog ----
  if (status === 'banned') return {
    severity: 'critical', dotClass: 'fs-pill--red', label: 'Banned',
    bannerText: 'Account banned by Telegram. Open @SpamBot in Telegram and follow the appeal prompts. After recovery, delete this account here and re-add it via "Add account → Enduser Account".',
    recoveryUrl: 'https://t.me/spambot', recoveryLabel: 'Open @SpamBot',
  };
  if (status === 'unauthorized') return {
    severity: 'high', dotClass: 'fs-pill--orange', label: 'Re-login required',
    bannerText: 'The Telegram session is no longer valid — the account owner most likely logged this device out via Telegram → Settings → Devices. Delete this account here and re-add it via "Add account → Enduser Account".',
    recoveryUrl: null, recoveryLabel: null,
  };
  if (status === 'flood_wait') {
    const blockedUntil = inbox.health_blocked_until || 0;
    const remaining = Math.max(0, blockedUntil - now.value);
    const isLong = remaining > LONG_FLOOD_WAIT_SECONDS;
    return {
      severity: isLong ? 'critical' : 'medium',
      dotClass: isLong ? 'fs-pill--red' : 'fs-pill--yellow',
      label: isLong ? `Long cooldown (${formatRemaining(remaining)})` : `Paused (${formatRemaining(remaining)})`,
      bannerText: `Account paused by Telegram due to rate limit. Remaining pause: ${formatRemaining(remaining)}.`,
      recoveryUrl: null, recoveryLabel: null,
    };
  }
  if (status === 'disconnected') return {
    severity: 'low', dotClass: 'fs-pill--orange', label: 'Disconnected',
    bannerText: 'Session disconnected — the watchdog tries to reconnect every 5 minutes. If it persists, delete the account and re-add it.',
    recoveryUrl: null, recoveryLabel: null,
  };
  // Unknown future status: still surface something rather than swallow it.
  return {
    severity: 'low', dotClass: 'fs-pill--orange', label: status,
    bannerText: `Unexpected health status: ${status}.`,
    recoveryUrl: null, recoveryLabel: null,
  };
};

const inboxesWithHealthIssue = computed(() =>
  // Only non-healthy MTProto inboxes drive the prominent banner above the
  // table. Healthy ones show only the green pill in the row's status column.
  (props.inboxes || []).filter(i => {
    const m = healthMeta(i);
    return m && m.severity !== 'ok';
  })
);

const checkInboxLink = async inboxId => {
  if (!inboxId) return;
  // Server is the source of truth (#91b): pull the connection flag + email down
  // (works on a fresh device — no localStorage needed), then, if connected,
  // ping the proxy with X-Fangate-Inbox to confirm the stored token still
  // works. The client never holds the token.
  const connected = await FangateAPI.hydrateInbox(inboxId);
  linkEmail[inboxId] = FangateAPI.getEmailForInbox(inboxId);
  webhookState[inboxId] = FangateAPI.getWebhookState(inboxId);
  if (!connected) { linkStatus[inboxId] = 'unlinked'; return; }
  linkStatus[inboxId] = 'checking';
  try {
    await FangateAPI.validateInbox(inboxId);
    linkStatus[inboxId] = 'linked';
  } catch (_) {
    linkStatus[inboxId] = 'rejected';
  }
};

// Kick off checks for every inbox the moment the table renders them. We only
// fire when the row first appears (linkStatus still undefined) so opening +
// closing the settings view doesn't spam Fangate on every navigation.
watch(
  () => props.inboxes,
  list => {
    (list || []).forEach(inbox => {
      if (inbox?.id && linkStatus[inbox.id] === undefined) {
        checkInboxLink(inbox.id);
      }
    });
  },
  { immediate: true, deep: false }
);

// ----- Help section ---------------------------------------------------------
const helpOpen = ref(false);
// Second help collapsible on the Chat-Accounts page — explains what a bot
// (and the Premium-Business connection that piggybacks on a bot) can and
// can't do, so admins understand the limits before they go set things up.
const botLimitsOpen = ref(false);
const webhookHelpOpen = ref(false);

// ----- Inline modals (no router navigation — keeps user on /fangate) -------
const editingInbox   = ref(null);     // inbox object being edited (null = closed)
const editForm       = reactive({ name: '', enable_auto_assignment: true, type: 'bot' });
const isSaving       = ref(false);
const saveError      = ref('');

const closeAllModals = () => {
  editingInbox.value = null;
  saveError.value    = '';
};

// "+ Add account" opens the FangateAddAccountModal — the 6-step wizard
// (Platform → Type → Token → Name → Agents → Fangate link) defined in the
// prototype. The router-based jump-to-Telegram-page is gone: the modal
// keeps the user in-place and supports the full Enduser/Bot/Channel type
// matrix. Dirty-tracking + non-dismissive backdrop live inside the modal.
const addAccountModalOpen = ref(false);
// When set, the Add-Account modal runs in RECONNECT mode for this inbox
// (re-auths the existing session in place instead of creating a new account).
const reconnectInbox = ref(null);
const goAddAccount = () => {
  closeMenus();
  reconnectInbox.value = null;
  addAccountModalOpen.value = true;
};
// Reconnect an existing (e.g. disconnected) Enduser inbox in place — opens the
// same connect-wizard; the backend re-auths THIS inbox, keeping its chats.
const goReconnect = inbox => {
  closeAllModals();
  reconnectInbox.value = inbox;
  addAccountModalOpen.value = true;
};
const onAccountCreated = () => {
  // Add: the inbox watcher picks up the new inbox. Reconnect: the wizard already
  // refreshed inboxes on success (health flips back to healthy). Close + clear.
  addAccountModalOpen.value = false;
  reconnectInbox.value = null;
};

// ----- Fangate vault per chat-account ---------------------------------------
// Token is stored in localStorage under `fangate_token_<inboxId>` (interim;
// moves server-side at the Hetzner backend). The edit modal lets the user
// paste the API token, validates it against /api/user, then saves it.
const fangateForm = reactive({
  token:     '',
  email:     '',       // Fangate account email (the vault's identity/username)
  saving:    false,
  error:     '',
  connected: false,    // true once /products lookup succeeds with the token
  // Live-Sales webhook (per connected vault): the URL to paste into Fangate's
  // "Register webhook" dialog + the optional signing secret it hands back.
  webhookUrl:       '',
  webhookSecret:    '',
  webhookSecretSet: false,
  savingSecret:     false,
  webhookCopied:    false,
});

// `connected` is a boolean — generated Fangate API keys don't have access to
// /api/user (it's hard-blocked BEFORE token verification), so we can't derive
// a creator name. The pill just confirms the link is live.
//
// On modal reopen we trust whatever's in localStorage and flip "Connected"
// synchronously. Validation already happened on Save; re-roundtripping on
// every open caused a "Not connected" flash that made the link look forgotten.
// If the token has been revoked since, the panel itself will surface the
// error when it fetches products / wallet.
const refreshFangateConnection = inbox => {
  fangateForm.token            = '';
  fangateForm.email            = '';
  fangateForm.error            = '';
  fangateForm.connected        = false;
  fangateForm.saving           = false;
  fangateForm.webhookUrl       = '';
  fangateForm.webhookSecret    = '';
  fangateForm.webhookSecretSet = false;
  fangateForm.webhookCopied    = false;
  if (!inbox?.id) return;
  // Pre-fill the Fangate email (account identity) regardless of link state, so
  // the agent can see/edit it whether or not a token is connected yet.
  fangateForm.email = FangateAPI.getEmailForInbox(inbox.id) || '';
  // The Live-Sales webhook is per chat-account, INDEPENDENT of the content vault
  // token — load its URL whether or not a token is connected.
  loadWebhookInfo(inbox.id);
  if (!FangateAPI.isInboxConnected(inbox.id)) return;
  // Token is server-only — show the connected state, not the secret. An empty
  // token field means "keep the existing token"; typing a new one replaces it.
  fangateForm.token     = '';
  fangateForm.connected = true;
};

// Fetch the Live-Sales webhook URL (+ whether a secret is on file) for this
// chat-account. Admin-only server-side, so non-admins just get an empty URL and
// the panel stays hidden.
const loadWebhookInfo = async id => {
  const { webhookUrl, webhookSecretSet } = await FangateAPI.getWebhookInfo(id);
  if (!editingInbox.value || editingInbox.value.id !== id) return;  // modal switched
  fangateForm.webhookUrl       = webhookUrl;
  fangateForm.webhookSecretSet = webhookSecretSet;
};

const saveFangateToken = async () => {
  if (!editingInbox.value) return;
  const id    = editingInbox.value.id;
  const token = (fangateForm.token || '').trim();
  const email = (fangateForm.email || '').trim();

  // Email-only edit on an already-connected vault: an empty token field means
  // "keep the existing token", so just persist the email.
  if (!token && fangateForm.connected) {
    FangateAPI.setEmailForInbox(id, email);
    linkEmail[id] = email;
    return;
  }
  if (!token) {
    fangateForm.error = 'Please paste your Fangate API token.';
    return;
  }
  // Nudge: connecting a vault without a webhook = no sales tracking. Warn only on
  // the FIRST connect (not a token-replace) and when no signing secret is on file.
  if (!fangateForm.connected && !fangateForm.webhookSecretSet) {
    const ok = await confirm({
      title: 'Connect without a webhook?',
      message: "You're connecting a Fangate vault but haven't registered a webhook yet. Only the webhook lets you track sales and get live notifications and reports — you can add it right after connecting. Connect anyway?",
      confirmLabel: 'Connect anyway',
    });
    if (!ok) return;
  }
  fangateForm.saving = true;
  fangateForm.error  = '';
  try {
    await FangateAPI.validateToken(token);
    // Save token + email together (single server PUT, no column race).
    FangateAPI.setLinkForInbox(id, { token, email });
    linkEmail[id] = email;
    fangateForm.connected = true;
    fangateForm.token = '';   // don't keep the secret in the form after saving
    linkStatus[id] = 'linked';
    loadWebhookInfo(id);      // the webhook URL is provisioned on connect
  } catch (e) {
    fangateForm.error = e?.message || 'Could not verify token.';
    linkStatus[id] = 'rejected';
  } finally {
    fangateForm.saving = false;
  }
};

const disconnectFangate = async () => {
  if (!editingInbox.value) return;
  if (!(await confirm({
    title: 'Disconnect Fangate?',
    message: 'Disconnect Fangate from this chat-account? The stored API token will be removed.',
    confirmLabel: 'Disconnect',
    danger: true,
  }))) return;
  FangateAPI.setTokenForInbox(editingInbox.value.id, '');
  fangateForm.token     = '';
  fangateForm.connected = false;
  fangateForm.error     = '';
  linkStatus[editingInbox.value.id] = 'unlinked';
  linkEmail[editingInbox.value.id]  = '';
  fangateForm.webhookUrl       = '';
  fangateForm.webhookSecret    = '';
  fangateForm.webhookSecretSet = false;
};

// Persist (or clear, when left blank) the Fangate signing secret for this
// vault's webhook. The HMAC receiver enforces a signature only when one is set.
const saveWebhookSecret = async () => {
  if (!editingInbox.value) return;
  const secret = (fangateForm.webhookSecret || '').trim();
  if (!secret) return;   // empty save is a no-op — use Remove to clear a secret
  const id = editingInbox.value.id;
  fangateForm.savingSecret = true;
  try {
    await FangateAPI.setWebhookSecret(id, secret);
    fangateForm.webhookSecretSet = true;
    fangateForm.webhookSecret = '';   // never keep the secret in the form
    // Saving a secret means the webhook is registered → reflect it in the overview.
    webhookState[id] = 'connected';
    FangateAPI.setWebhookStateCache(id, 'connected');
  } catch (_) {
    /* best-effort; the field stays editable so the admin can retry */
  } finally {
    fangateForm.savingSecret = false;
  }
};

// Clear a stored signing secret → the receiver falls back to path-token-only auth.
const removeWebhookSecret = async () => {
  if (!editingInbox.value) return;
  const id = editingInbox.value.id;
  fangateForm.savingSecret = true;
  try {
    await FangateAPI.setWebhookSecret(id, '');
    fangateForm.webhookSecretSet = false;
    fangateForm.webhookSecret = '';
    // Optimistic; the next overview hydrate recomputes from delivery history.
    webhookState[id] = 'not_connected';
    FangateAPI.setWebhookStateCache(id, 'not_connected');
  } catch (_) {
    /* best-effort */
  } finally {
    fangateForm.savingSecret = false;
  }
};

// Copy the webhook URL with brief "Copied" feedback (field is selectable too).
const copyWebhookUrl = async () => {
  if (!fangateForm.webhookUrl) return;
  try {
    await navigator.clipboard.writeText(fangateForm.webhookUrl);
    fangateForm.webhookCopied = true;
    setTimeout(() => { fangateForm.webhookCopied = false; }, 1500);
  } catch (_) { /* clipboard unavailable — fall back to manual select/copy */ }
};

const goEditInbox = inbox => {
  closeMenus();
  if (!inbox) return;
  editingInbox.value = inbox;
  editForm.name                    = inbox.name || '';
  editForm.enable_auto_assignment  = isActive(inbox);
  // For TelegramUser inboxes there's no account_type to preselect — the
  // template hides the dropdown anyway. Use 'mtproto' as a sentinel so any
  // stray reader can tell the inbox apart from a real 'bot'.
  editForm.type                    = inbox.channel_type === 'Channel::TelegramUser'
                                       ? 'mtproto'
                                       : (inbox.account_type || 'bot');
  saveError.value = '';
  refreshFangateConnection(inbox);
};

const saveInbox = async () => {
  if (!editingInbox.value) return;
  isSaving.value = true;
  saveError.value = '';
  try {
    // account_type is a Channel::Telegram column only; Channel::TelegramUser
    // (MTProto/Enduser) has no such field, and sending it would just be a no-op
    // permitted-params drop. Skip the channel payload entirely for that case so
    // the API call stays clean.
    const isTelegramUser = editingInbox.value?.channel_type === 'Channel::TelegramUser';
    const payload = {
      id:                     editingInbox.value.id,
      formData:               false,                  // send JSON, not FormData
      name:                   editForm.name,
      enable_auto_assignment: editForm.enable_auto_assignment,
    };
    if (!isTelegramUser) {
      // Persist the account kind server-side — permitted as a channel param via
      // Channel::Telegram::EDITABLE_ATTRS. The EDIT_INBOXES commit updates the
      // Vuex inbox, so the Type column (reads inbox.account_type) re-renders.
      payload.channel = { account_type: editForm.type };
    }
    await store.dispatch('inboxes/updateInbox', payload);
    // Persist the Fangate email (account identity) too, so editing just the
    // email + Save changes works without re-pasting the token. Guarded so we
    // don't hit the endpoint for an unlinked account with no email entered.
    const fgEmail = (fangateForm.email || '').trim();
    if (fgEmail || fangateForm.connected) {
      FangateAPI.setEmailForInbox(editingInbox.value.id, fgEmail);
      linkEmail[editingInbox.value.id] = fgEmail;
    }
    closeAllModals();
  } catch (e) {
    saveError.value = e?.message || 'Failed to save changes.';
  } finally {
    isSaving.value = false;
  }
};

const goDeleteInbox = async inbox => {
  closeMenus();
  if (!inbox) return;
  if (!(await confirm({
    title: 'Delete chat-account?',
    message: `Delete chat-account "${inbox.name}"?\n\n` +
      `Existing chats stay in the inbox archive, but the account stops ` +
      `syncing and cannot send or receive new messages.`,
    confirmLabel: 'Delete',
    danger: true,
  }))) return;
  try {
    await store.dispatch('inboxes/delete', inbox.id);
  } catch (e) {
    useAlert('Failed to delete: ' + (e?.message ?? e));
  }
};

const toggleActive = async inbox => {
  closeMenus();
  const next = !isActive(inbox);
  // Optimistic local mutation so UI flips immediately
  inbox.enable_auto_assignment = next;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: inbox.id,
      formData: false,
      enable_auto_assignment: next,
    });
  } catch (_) {
    // Rollback on failure
    inbox.enable_auto_assignment = !next;
  }
};

const openAgentPicker = (e, inbox) => {
  closeMenus();
  emit('open-agent-picker', { event: e, inbox });
};

// ============================================================================
// Bots & AI state (local until a backend model lands)
// ============================================================================
// Placeholder bot list — replace with real `agent_bots` API once wired
const bots = ref([]);

// ChatGPT integration state. Source of truth is the server-side `openai`
// integration hook (via useChatGptState → Vuex integrations store). We mirror
// it into a local `aiState` reactive purely for this card's display. The raw
// key is never kept in the browser — only a masked preview derived from the hook.
const {
  connected: aiConnected,
  openaiHook,
  keyMasked: aiKeyMasked,
  paused: aiPaused,
  setPaused,
} = useChatGptState();

const aiState = reactive({ connected: false, active: true, keyMasked: '' });
const syncAiState = () => {
  aiState.connected = aiConnected.value;
  aiState.active    = !aiPaused.value;
  aiState.keyMasked = aiKeyMasked.value;
};
watch([aiConnected, aiPaused, openaiHook], syncAiState, { immediate: true });

// Make sure the integrations store (incl. the openai hook) is hydrated for this
// page even if the user lands directly on Settings (FangateLayout also fetches).
onMounted(() => { store.dispatch('integrations/get'); });

// Backend wiring is live (key stored in the openai hook, Suggest-reply hits
// Captain::ReplySuggestionService). No longer "coming soon".
const chatgptComingSoon = false;

// AI connect modal
const aiModalOpen   = ref(false);
const aiKeyInput    = ref('');
const aiSaving      = ref(false);
const aiError       = ref('');

const openAIConnect = () => {
  aiKeyInput.value   = '';
  aiError.value      = '';
  aiModalOpen.value  = true;
};
const closeAIConnect = () => { aiModalOpen.value = false; };

const confirmAIConnect = async () => {
  const key = aiKeyInput.value.trim();
  if (!key) { aiError.value = 'Please enter your API key'; return; }
  if (!key.startsWith('sk-')) { aiError.value = 'Invalid key — must start with sk-'; return; }
  aiSaving.value = true;
  aiError.value  = '';
  try {
    // The `openai` integration allows a single hook — replace any existing one
    // so "Edit" re-paste cleanly swaps the key.
    if (openaiHook.value?.id) {
      await store.dispatch('integrations/deleteHook', { appId: 'openai', hookId: openaiHook.value.id });
    }
    await store.dispatch('integrations/createHook', {
      app_id: 'openai',
      settings: { api_key: key },
    });
    setPaused(false);          // a fresh connect is active by default
    aiModalOpen.value = false;
    aiKeyInput.value  = '';
  } catch (_) {
    aiError.value = 'Could not save the key. Please check it and try again.';
  } finally {
    aiSaving.value = false;
  }
};

const disconnectAI = async () => {
  if (!(await confirm({
    title: 'Disconnect ChatGPT?',
    message: 'Disconnect ChatGPT? Suggest-reply will stop working in chats.',
    confirmLabel: 'Disconnect',
    danger: true,
  }))) return;
  if (!openaiHook.value?.id) return;
  try {
    await store.dispatch('integrations/deleteHook', { appId: 'openai', hookId: openaiHook.value.id });
  } catch (_) { /* if it fails the card simply stays connected — no data lost */ }
};
// Pause/resume is a local toggle: keeps the key connected but hides the
// Suggest-reply button in chats until resumed.
const toggleAIActive = () => { setPaused(!aiPaused.value); };

// Bots help collapsible
const botsHelpOpen = ref(false);
const aiHelpOpen   = ref(false);

// ============================================================================
// Bot builder — keyword-driven Chatwoot Automation Rules
// Each Creator-Chat bot IS a Chatwoot AutomationRule under the hood, scoped
// by the user to a specific Telegram inbox. The rule fires `send_message`
// via Chatwoot's regular pipeline → Channel::Telegram#send_message → the bot
// auto-replies on Telegram (and the cross-inbox mirror / business_connection
// handling kicks in automatically because the same send path is used).
// ============================================================================
import AutomationsAPI from 'dashboard/api/automation';

const botModalOpen   = ref(false);
const botModalMode   = ref('chooser'); // 'chooser' | 'create' | 'pick'
const botEditingId   = ref(null);      // null = create-new; else = patching this rule id
const botSaving      = ref(false);
const botError       = ref('');
const botsLoading    = ref(false);

// Scope = which conversation kinds the bot fires in. Mapped to a
// chat_type filter on conversation.additional_attributes (registered in
// lib/filters/filter_keys.yml). 'all' = no chat_type condition.
const BOT_SCOPE_OPTIONS = [
  { value: 'all',      label: 'All chats in this inbox (DMs + groups + channels)' },
  { value: 'dms',      label: 'DMs only — private 1-on-1 chats' },
  { value: 'groups',   label: 'Groups only — group & supergroup chats' },
  { value: 'channels', label: 'Channels only — broadcast channels' },
];

// Chatwoot AutomationRule event triggers. We expose the four most useful in
// CRM-bot context. `message_created` keeps the keyword filter visible; the
// conversation_* events hide the keyword field because they fire on
// conversation lifecycle, not on individual message content.
const BOT_EVENT_OPTIONS = [
  { value: 'message_created',      label: '💬 Incoming message — fire when contact sends a message (keyword-based)',  needsKeyword: true  },
  { value: 'conversation_created', label: '🆕 New conversation — fire ONCE when a chat first opens (welcome flow)',    needsKeyword: false },
  { value: 'conversation_opened',  label: '📂 Conversation reopened — fire when a resolved chat is opened again',     needsKeyword: false },
  { value: 'conversation_updated', label: '🔁 Conversation updated — fire when status/labels/priority change',         needsKeyword: false },
];

// ============================================================================
// Action definitions — the catalogue of things a bot can do when its
// trigger fires. Each maps to one of Chatwoot's AutomationRule action_names
// (see app/services/automation_rules/action_service.rb + base ActionService).
// Each entry also tells the UI which param control to render and how to
// serialize the params to Chatwoot's action_params array.
// ============================================================================
const PRIORITY_OPTIONS = [
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
const STATUS_OPTIONS = [
  { value: 'open',     label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'pending',  label: 'Pending' },
];
const ACTION_DEFS = [
  // --- Messaging
  {
    type: 'send_message', icon: '💬', label: 'Send reply',
    desc: 'Send a text message into the chat (uses the linked Telegram inbox\'s send path).',
    paramUI: 'textarea', paramKey: 'text', default: '',
    serialize: (p) => [String(p.text || '').trim()],
    deserialize: (params) => ({ text: Array.isArray(params) ? (params[0] || '') : (params || '') }),
    validate: (p) => p.text?.trim() ? null : 'Reply text is required.',
  },
  {
    type: 'add_private_note', icon: '📝', label: 'Add private note',
    desc: 'Adds an internal note visible to agents only — never sent to the contact.',
    paramUI: 'textarea', paramKey: 'text', default: '',
    serialize: (p) => [String(p.text || '').trim()],
    deserialize: (params) => ({ text: Array.isArray(params) ? (params[0] || '') : (params || '') }),
    validate: (p) => p.text?.trim() ? null : 'Note text is required.',
  },
  // --- Organisation
  {
    type: 'add_label', icon: '🏷️', label: 'Add label',
    desc: 'Tag the conversation with one or more labels.',
    paramUI: 'labels', paramKey: 'labels', default: [],
    serialize: (p) => Array.isArray(p.labels) ? p.labels : [],
    deserialize: (params) => ({ labels: Array.isArray(params) ? params.map(String) : [] }),
    validate: (p) => (p.labels?.length ? null : 'Pick at least one label.'),
  },
  {
    type: 'remove_label', icon: '✂️', label: 'Remove label',
    desc: 'Strip one or more labels off the conversation.',
    paramUI: 'labels', paramKey: 'labels', default: [],
    serialize: (p) => Array.isArray(p.labels) ? p.labels : [],
    deserialize: (params) => ({ labels: Array.isArray(params) ? params.map(String) : [] }),
    validate: (p) => (p.labels?.length ? null : 'Pick at least one label.'),
  },
  {
    type: 'assign_agent', icon: '👤', label: 'Assign agent',
    desc: 'Auto-assign the conversation to a specific agent.',
    paramUI: 'agent', paramKey: 'agentId', default: null,
    serialize: (p) => p.agentId ? [Number(p.agentId)] : [],
    deserialize: (params) => ({ agentId: Array.isArray(params) ? (params[0] ?? null) : (params ?? null) }),
    validate: (p) => p.agentId ? null : 'Pick an agent.',
  },
  {
    type: 'remove_assigned_agent', icon: '🧹', label: 'Unassign agent',
    desc: 'Remove the current assignee from the conversation.',
    paramUI: 'none', paramKey: null,
    serialize: () => [],
    deserialize: () => ({}),
    validate: () => null,
  },
  {
    type: 'change_priority', icon: '🔥', label: 'Change priority',
    desc: 'Set conversation priority (low / medium / high / urgent).',
    paramUI: 'select', paramKey: 'priority', default: 'medium', options: PRIORITY_OPTIONS,
    serialize: (p) => [p.priority || 'medium'],
    deserialize: (params) => ({ priority: Array.isArray(params) ? (params[0] || 'medium') : (params || 'medium') }),
    validate: () => null,
  },
  {
    type: 'change_status', icon: '🔁', label: 'Change status',
    desc: 'Move conversation to open / resolved / pending.',
    paramUI: 'select', paramKey: 'status', default: 'resolved', options: STATUS_OPTIONS,
    serialize: (p) => [p.status || 'resolved'],
    deserialize: (params) => ({ status: Array.isArray(params) ? (params[0] || 'resolved') : (params || 'resolved') }),
    validate: () => null,
  },
  // --- Conversation lifecycle (no params)
  { type: 'resolve_conversation', icon: '✅', label: 'Resolve conversation', desc: 'Mark this conversation as resolved.',
    paramUI: 'none', paramKey: null, serialize: () => [], deserialize: () => ({}), validate: () => null },
  { type: 'open_conversation',    icon: '📂', label: 'Reopen conversation', desc: 'Reopen if it was resolved/pending.',
    paramUI: 'none', paramKey: null, serialize: () => [], deserialize: () => ({}), validate: () => null },
  { type: 'mute_conversation',    icon: '🔕', label: 'Mute conversation',  desc: 'Silence notifications for this conversation.',
    paramUI: 'none', paramKey: null, serialize: () => [], deserialize: () => ({}), validate: () => null },
  // --- External
  {
    type: 'send_webhook_event', icon: '🔗', label: 'Send webhook',
    desc: 'POST a Chatwoot webhook event to an external URL (Zapier / n8n / your own backend).',
    paramUI: 'text', paramKey: 'url', default: '',
    serialize: (p) => [String(p.url || '').trim()],
    deserialize: (params) => ({ url: Array.isArray(params) ? (params[0] || '') : (params || '') }),
    validate: (p) => /^https?:\/\//i.test(p.url || '') ? null : 'URL must start with http:// or https://',
  },
  // --- Telegram-specific custom actions (extend Chatwoot's ActionService)
  {
    type: 'send_dm_to_user', icon: '📨', label: 'Send DM to user',
    desc: 'Send a private DM to the user that triggered the rule. If the user has no existing DM with this bot (never /start\'d it), the action no-ops with a warning in the Rails log — bots can\'t initiate DMs to strangers.',
    paramUI: 'textarea', paramKey: 'text', default: '',
    serialize: (p) => [String(p.text || '').trim()],
    deserialize: (params) => ({ text: Array.isArray(params) ? (params[0] || '') : (params || '') }),
    validate: (p) => p.text?.trim() ? null : 'Message text is required.',
  },
  {
    type: 'pin_telegram_message', icon: '📌', label: 'Pin triggering message',
    desc: 'Pin the message that triggered this rule in its chat. Bot needs can_pin_messages permission in the target group/channel.',
    paramUI: 'none', paramKey: null,
    serialize: () => [],
    deserialize: () => ({}),
    validate: () => null,
  },
  {
    type: 'create_telegram_invite_link', icon: '🎟️', label: 'Create invite link & send',
    desc: 'Create a fresh single-use invite link to a target Telegram group/channel and send it as a message. Bot must be admin (with can_invite_users) in the target chat.',
    paramUI: 'inviteLink',
    paramKeys: ['chatId', 'memberLimit', 'expireSeconds', 'template'],
    default: { chatId: '', memberLimit: 1, expireSeconds: 3600, template: 'Here is your invite link: {{link}}' },
    serialize: (p) => [{
      chat_id:        String(p.chatId || '').trim(),
      member_limit:   Number(p.memberLimit) || 0,
      expire_seconds: Number(p.expireSeconds) || 0,
      send_template:  String(p.template || 'Here is your invite link: {{link}}'),
    }],
    deserialize: (params) => {
      const cfg = Array.isArray(params) ? (params[0] || {}) : (params || {});
      return {
        chatId:        cfg.chat_id || '',
        memberLimit:   cfg.member_limit ?? 1,
        expireSeconds: cfg.expire_seconds ?? 3600,
        template:      cfg.send_template || 'Here is your invite link: {{link}}',
      };
    },
    validate: (p) => {
      if (!String(p.chatId || '').trim()) return 'Target chat_id is required (negative number for groups/channels, e.g. -1001234567890).';
      if (!String(p.template || '').includes('{{link}}')) return 'Message template must include the {{link}} placeholder.';
      return null;
    },
  },
];
const ACTION_DEF_BY_TYPE = Object.fromEntries(ACTION_DEFS.map(d => [d.type, d]));
// Built outside the template because writing `{{link}}` literally in a Vue
// template string triggers the mustache parser even when wrapped in another
// mustache — produces "Unterminated string constant" at HMR time. Computing
// here keeps the template free of nested-mustache traps.
const inviteLinkPlaceholder = 'Here is your invite link: {{link}}';
let _actionUidCounter = 0;
const newActionUid = () => `a${++_actionUidCounter}`;
const makeAction = (type) => {
  const def = ACTION_DEF_BY_TYPE[type] || ACTION_DEFS[0];
  // Three param shapes: (1) single-key actions with paramKey + default scalar/array;
  // (2) multi-key actions with paramKeys + default object; (3) zero-param actions.
  let params;
  if (def.paramKey) {
    params = { [def.paramKey]: structuredClone(def.default) };
  } else if (def.paramKeys && def.default && typeof def.default === 'object') {
    params = structuredClone(def.default);
  } else {
    params = {};
  }
  return { uid: newActionUid(), type: def.type, params };
};

const botForm = reactive({
  name:     '',
  inboxId:  null,            // single Telegram inbox to scope the rule
  event:    'message_created', // see BOT_EVENT_OPTIONS — non-message events skip the keyword filter
  scope:    'all',           // 'all' | 'dms' | 'groups' | 'channels'
  keywords: [],              // array of strings; matched as Content contains ANY
  actions:  [],              // ordered list of { uid, type, params } — see ACTION_DEFS
  active:   true,
});
// Helper used by template + validate path: does the current event need the
// keyword-trigger field, or is it lifecycle-only?
const currentEventNeedsKeyword = computed(() =>
  BOT_EVENT_OPTIONS.find(e => e.value === botForm.event)?.needsKeyword ?? true
);
const newKeywordInput = ref('');

// Telegram inboxes only — bots wired to non-Telegram inboxes wouldn't work
// with the Telegram-specific send path described above.
const botInboxOptions = computed(() =>
  props.inboxes.filter(i => platformOf(i) === 'telegram')
);

// Selected inbox name resolver — used to render Linked Accounts column.
const inboxNameById = (id) => {
  const ix = props.inboxes.find(i => Number(i.id) === Number(id));
  return ix ? (ix.name || `Inbox #${ix.id}`) : `Inbox #${id}`;
};

// Per-account expand/collapse state. Each entry = a botsByInbox group key
// the user has explicitly COLLAPSED — the default is expanded so bots are
// visible on first visit. Persisted in localStorage so reloads remember.
const LS_BOT_COLLAPSED_KEY = 'fangate_bot_collapsed_accounts';
const collapsedAccounts = ref(new Set(
  (() => {
    try { return JSON.parse(localStorage.getItem(LS_BOT_COLLAPSED_KEY) || '[]'); }
    catch (_) { return []; }
  })()
));
const persistCollapsed = () => {
  try {
    localStorage.setItem(LS_BOT_COLLAPSED_KEY, JSON.stringify([...collapsedAccounts.value]));
  } catch (_) { /* localStorage may be disabled — ignore */ }
};
const isAccountExpanded = (key) => !collapsedAccounts.value.has(String(key));
const toggleAccountExpand = (key) => {
  const k = String(key);
  const next = new Set(collapsedAccounts.value);
  if (next.has(k)) next.delete(k); else next.add(k);
  collapsedAccounts.value = next;
  persistCollapsed();
};

// Group bot rows by their linked Telegram inbox so the table shows one
// section per account ("Sven's bots", "Henning's bots", …). Rules with no
// inbox_id condition land in a trailing "Other / global" bucket so they
// stay visible without ambiguity.
const botsByInbox = computed(() => {
  const buckets = new Map();
  for (const b of bots.value) {
    const inboxIds = (b.raw?.conditions || [])
      .find(c => c.attribute_key === 'inbox_id')?.values || [];
    const key = inboxIds.length ? Number(inboxIds[0]) : '__global__';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(b);
  }
  const named = [];
  for (const [key, items] of buckets) {
    if (key === '__global__') continue;
    named.push({ key, name: inboxNameById(key), items });
  }
  named.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  if (buckets.has('__global__')) {
    named.push({ key: '__global__', name: 'Other / global rules', items: buckets.get('__global__') });
  }
  return named;
});

// Map Chatwoot AutomationRule → row shape the bots table renders.
// `scope` is derived from any chat_type condition on the rule so the table
// surfaces it as the "Mode" pill ("All chats", "DMs", "Groups", "Channels").
const SCOPE_PILL = {
  all:      { icon: '🌐', label: 'All chats' },
  dms:      { icon: '💬', label: 'DMs only' },
  groups:   { icon: '👥', label: 'Groups only' },
  channels: { icon: '📢', label: 'Channels only' },
};
const ruleToBotRow = (rule) => {
  const conds = Array.isArray(rule.conditions) ? rule.conditions : [];
  const inboxCond   = conds.find(c => c.attribute_key === 'inbox_id');
  const contentCond = conds.find(c => c.attribute_key === 'content');
  const linkedIds   = inboxCond ? (inboxCond.values || []) : [];
  const keywords    = contentCond ? (contentCond.values || []) : [];
  const scope       = conditionsToScope(conds);
  // Action overview = just the type + icon + short label of each action so
  // the table shows what a bot DOES at a glance without unfolding the edit
  // modal. Falls back to a generic "?" chip for unknown action_names so
  // future Chatwoot/custom actions stay visible (not silently dropped).
  const actionSummary = (Array.isArray(rule.actions) ? rule.actions : [])
    .map(a => {
      const def = ACTION_DEF_BY_TYPE[a?.action_name];
      return def
        ? { type: def.type, icon: def.icon, label: def.label }
        : { type: a?.action_name || 'unknown', icon: '❓', label: a?.action_name || 'Unknown' };
    });
  return {
    id:             rule.id,
    name:           rule.name,
    handle:         keywords.length ? `Triggers: ${keywords.join(', ')}` : 'No keywords',
    mode:           'inline',
    scope,
    scopeLabel:     SCOPE_PILL[scope].label,
    scopeIcon:      SCOPE_PILL[scope].icon,
    linkedAccounts: linkedIds.map(inboxNameById),
    rules:          keywords,
    actionSummary,
    active:         !!rule.active,
    raw:            rule,
  };
};

const loadBots = async () => {
  botsLoading.value = true;
  try {
    const resp = await AutomationsAPI.get();
    const list = resp?.data?.payload ?? resp?.data?.data?.payload ?? resp?.data ?? [];
    bots.value = list.map(ruleToBotRow);
  } catch (e) {
    // leave bots as-is; surface error in console for debugging.
    // eslint-disable-next-line no-console
    console.warn('[FangateSettings] loadBots failed', e?.response?.data || e?.message);
  } finally {
    botsLoading.value = false;
  }
};

const resetBotForm = () => {
  botForm.name     = '';
  botForm.inboxId  = botInboxOptions.value[0]?.id ?? null;
  botForm.event    = 'message_created';
  botForm.scope    = 'all';
  botForm.keywords = [];
  // Seed every new bot with a single send_message action so the most common
  // case (keyword → reply) is one click away.
  botForm.actions  = [makeAction('send_message')];
  botForm.active   = true;
  newKeywordInput.value = '';
  botError.value   = '';
  botEditingId.value = null;
};

// Action list manipulation -----------------------------------------------------
// Currently-selected type in the "Add action" picker. Defaults to send_message
// because that's the most common follow-up the user wants after a label/assign.
const newActionType = ref('send_message');
// Toggle a label title in a label-multi-select action's params.
const toggleActionLabel = (action, title) => {
  const list = Array.isArray(action.params.labels) ? action.params.labels : [];
  const idx = list.indexOf(title);
  if (idx === -1) action.params.labels = [...list, title];
  else action.params.labels = list.filter(t => t !== title);
};
const addBotAction = (type = 'send_message') => {
  botForm.actions.push(makeAction(type));
};
const removeBotAction = (idx) => {
  botForm.actions.splice(idx, 1);
};
const moveBotAction = (idx, delta) => {
  const j = idx + delta;
  if (j < 0 || j >= botForm.actions.length) return;
  const [item] = botForm.actions.splice(idx, 1);
  botForm.actions.splice(j, 0, item);
};
// Switching action type wipes the params (the param shape differs per type).
const changeBotActionType = (idx, newType) => {
  botForm.actions[idx] = makeAction(newType);
};
const defForAction = (a) => ACTION_DEF_BY_TYPE[a?.type] || ACTION_DEFS[0];

const openBotModal = () => {
  resetBotForm();
  botModalMode.value = 'chooser';
  botModalOpen.value = true;
};
const closeBotModal = () => {
  botModalOpen.value = false;
  botModalMode.value = 'chooser';
};
const switchBotMode = (mode) => {
  botError.value = '';
  if (mode === 'create') resetBotForm();
  botModalMode.value = mode;
};

const addBotKeyword = () => {
  const kw = newKeywordInput.value.trim();
  if (!kw) return;
  if (!botForm.keywords.includes(kw)) botForm.keywords.push(kw);
  newKeywordInput.value = '';
};
const removeBotKeyword = (idx) => {
  botForm.keywords.splice(idx, 1);
};

// Convert a single Chatwoot action object → builder shape, preserving unknown
// action_names so they survive a round-trip (we still render them as a generic
// row with a "?" badge instead of dropping the user's data on edit).
const importBotAction = (a) => {
  const def = ACTION_DEF_BY_TYPE[a?.action_name];
  if (def) {
    return { uid: newActionUid(), type: def.type, params: def.deserialize(a.action_params) };
  }
  // Unknown action — preserve the raw payload so saving doesn't drop it.
  return { uid: newActionUid(), type: a?.action_name || '__unknown__', params: { __raw: a?.action_params } };
};

// Open an existing rule in the form (edit mode). Used both from "pick existing"
// flow and from the table kebab.
const editBot = (row) => {
  botEditingId.value = row.id;
  botForm.name       = row.name || '';
  const inboxIds     = (row.raw?.conditions || [])
                        .find(c => c.attribute_key === 'inbox_id')?.values || [];
  botForm.inboxId    = inboxIds[0] ?? null;
  botForm.event      = row.raw?.event_name || 'message_created';
  botForm.scope      = conditionsToScope(row.raw?.conditions);
  botForm.keywords   = [...(row.rules || [])];
  const rawActions   = Array.isArray(row.raw?.actions) ? row.raw.actions : [];
  botForm.actions    = rawActions.length
    ? rawActions.map(importBotAction)
    : [makeAction('send_message')];
  botForm.active     = !!row.active;
  newKeywordInput.value = '';
  botError.value     = '';
  botModalMode.value = 'create';
  botModalOpen.value = true;
};

const deleteBot = async (row) => {
  if (!row?.id) return;
  if (!(await confirm({
    title: 'Delete bot?',
    message: `Delete bot "${row.name}"? This removes the underlying Chatwoot automation rule.`,
    confirmLabel: 'Delete',
    danger: true,
  }))) return;
  try {
    await AutomationsAPI.delete(row.id);
    await loadBots();
  } catch (e) {
    useAlert(`Failed to delete: ${e?.response?.data?.message || e?.message || 'unknown error'}`);
  }
};

// Scope → chat_type condition. Returns null when scope='all' (no condition
// added at all, so the rule fires for every conversation in the inbox).
// 'groups' covers both `group` and `supergroup` (Telegram migrates groups
// to supergroups silently when they grow, so always include both).
const scopeToChatTypeCondition = (scope) => {
  switch (scope) {
    case 'dms':
      return { attribute_key: 'chat_type', filter_operator: 'equal_to', values: ['private'], query_operator: 'and' };
    case 'groups':
      // Chatwoot's FilterService doesn't implement is_in (falls through to
      // equals-with-array which always fails). Use `contains 'group'` instead:
      // matches both 'group' AND 'supergroup' (both contain the substring)
      // but not 'private' or 'channel'. Safe because Telegram only emits
      // those four chat_type values.
      return { attribute_key: 'chat_type', filter_operator: 'contains', values: ['group'], query_operator: 'and' };
    case 'channels':
      return { attribute_key: 'chat_type', filter_operator: 'equal_to', values: ['channel'], query_operator: 'and' };
    default:
      return null;
  }
};

// Inverse: read scope back from an existing rule's conditions.
// Handles both the current shape (contains 'group') AND the legacy shape
// (is_in ['group','supergroup']) so editing a bot that was saved before
// this fix still maps to scope='groups' correctly.
const conditionsToScope = (conditions) => {
  const ct = (conditions || []).find(c => c.attribute_key === 'chat_type');
  if (!ct) return 'all';
  const op   = ct.filter_operator;
  const vals = (ct.values || []).map(String);
  if (op === 'contains' && vals.includes('group'))            return 'groups';
  if (vals.includes('private')  && vals.length === 1)         return 'dms';
  if (vals.includes('channel')  && vals.length === 1)         return 'channels';
  if (vals.includes('group') || vals.includes('supergroup'))  return 'groups';
  return 'all';
};

// Convert form state → Chatwoot AutomationRule payload.
// Conditions: inbox_id == X AND (optional chat_type filter) AND message_type == incoming AND content contains ANY keyword.
// Actions: serialised from botForm.actions[] via each ACTION_DEF's serialize().
const buildRulePayload = () => {
  const conditions = [
    { attribute_key: 'inbox_id', filter_operator: 'equal_to', values: [Number(botForm.inboxId)], query_operator: 'and' },
  ];
  const scopeCond = scopeToChatTypeCondition(botForm.scope);
  if (scopeCond) conditions.push(scopeCond);
  // Only attach message-specific conditions when the trigger is a message
  // event. conversation_created / opened / updated don't have a message
  // object yet, so message_type/content conditions would never match.
  if (currentEventNeedsKeyword.value) {
    conditions.push(
      { attribute_key: 'message_type', filter_operator: 'equal_to', values: ['incoming'], query_operator: 'and' },
      { attribute_key: 'content',      filter_operator: 'contains', values: botForm.keywords, query_operator: null }
    );
  } else {
    // Last condition's query_operator must be null per Chatwoot's
    // condition_filter_service contract — patch the trailing one.
    conditions[conditions.length - 1].query_operator = null;
  }
  const actions = botForm.actions.map(a => {
    const def = ACTION_DEF_BY_TYPE[a.type];
    if (def) return { action_name: def.type, action_params: def.serialize(a.params) };
    // Unknown / preserved-on-edit action — pass raw through.
    return { action_name: a.type, action_params: a.params?.__raw ?? [] };
  });
  return {
    name:        botForm.name.trim(),
    description: 'Creator-Chat keyword bot',
    event_name:  botForm.event,
    conditions,
    actions,
    active: !!botForm.active,
  };
};

const saveBot = async () => {
  botError.value = '';
  if (!botForm.name.trim())            { botError.value = 'Bot name is required.'; return; }
  if (!botForm.inboxId)                { botError.value = 'Pick a Telegram inbox.'; return; }
  if (currentEventNeedsKeyword.value && !botForm.keywords.length) {
    botError.value = 'Add at least one trigger keyword.'; return;
  }
  if (!botForm.actions.length)         { botError.value = 'Add at least one action.'; return; }
  for (let i = 0; i < botForm.actions.length; i++) {
    const a   = botForm.actions[i];
    const def = ACTION_DEF_BY_TYPE[a.type];
    if (!def) continue; // preserved unknown — skip validation
    const err = def.validate(a.params);
    if (err) { botError.value = `Action ${i + 1} (${def.label}): ${err}`; return; }
  }

  botSaving.value = true;
  try {
    const payload = buildRulePayload();
    if (botEditingId.value) {
      await AutomationsAPI.update(botEditingId.value, payload);
    } else {
      await AutomationsAPI.create(payload);
    }
    closeBotModal();
    await loadBots();
  } catch (e) {
    botError.value = e?.response?.data?.message || e?.message || 'Save failed';
  } finally {
    botSaving.value = false;
  }
};

// Auto-load whenever the user opens the Bots & AI page.
watch(
  () => props.view,
  (v) => { if (v === 'settings-bots') loadBots(); },
  { immediate: true }
);

// ============================================================================
// Labels CRUD state
// ============================================================================
const LABEL_COLORS = [
  '#0d9488', // teal (accent)
  '#5b21b6', // purple
  '#dc2626', // red
  '#ea580c', // orange
  '#d97706', // amber
  '#65a30d', // lime
  '#0891b2', // cyan
  '#1d4ed8', // blue
  '#9333ea', // violet
  '#db2777', // pink
  '#475569', // slate
  '#1f2937', // dark gray
];

const labelModalOpen = ref(false);
const labelEditing   = ref(null);   // null = create-mode, else editing this label obj
const labelForm      = reactive({ title: '', color: LABEL_COLORS[0], description: '' });
const labelSaving    = ref(false);
const labelError     = ref('');

const openLabelCreate = () => {
  labelEditing.value   = null;
  labelForm.title       = '';
  labelForm.color       = LABEL_COLORS[0];
  labelForm.description = '';
  labelError.value      = '';
  labelModalOpen.value  = true;
};

const openLabelEdit = lbl => {
  labelEditing.value    = lbl;
  labelForm.title       = lbl.title || '';
  labelForm.color       = lbl.color || LABEL_COLORS[0];
  labelForm.description = lbl.description || '';
  labelError.value      = '';
  labelModalOpen.value  = true;
};

const closeLabelModal = () => {
  labelModalOpen.value = false;
  labelEditing.value   = null;
};

// Label titles must be ONE connected word (Chatwoot uses the title as an
// identifier in filters/automations). Letters, numbers, - and _ are allowed;
// spaces are not. Live feedback while typing + a hard stop on save.
const labelTitleIssue = computed(() => {
  const t = labelForm.title.trim();
  if (!t) return '';
  if (/\s/.test(t)) {
    return "Labels must be one connected word — spaces aren't allowed. Use - or _ instead (e.g. vip-fans).";
  }
  if (!/^[\p{L}\p{N}_-]+$/u.test(t)) {
    return 'Only letters, numbers, - and _ are allowed in a label name.';
  }
  return '';
});

const saveLabel = async () => {
  const title = labelForm.title.trim();
  if (!title) { labelError.value = 'Label name is required.'; return; }
  if (labelTitleIssue.value) { labelError.value = labelTitleIssue.value; return; }
  labelSaving.value = true;
  labelError.value  = '';
  try {
    if (labelEditing.value) {
      await store.dispatch('labels/update', {
        id:              labelEditing.value.id,
        title:           title,
        color:           labelForm.color,
        description:     labelForm.description,
        show_on_sidebar: true,
      });
    } else {
      await store.dispatch('labels/create', {
        title:           title,
        color:           labelForm.color,
        description:     labelForm.description,
        show_on_sidebar: true,
      });
    }
    closeLabelModal();
  } catch (e) {
    labelError.value = e?.message || 'Failed to save label.';
  } finally {
    labelSaving.value = false;
  }
};

const deleteLabel = async lbl => {
  if (!(await confirm({
    title: 'Delete label?',
    message: `Delete label "${lbl.title}"?\n\n` +
      `It will be removed from all chats it's currently assigned to.`,
    confirmLabel: 'Delete',
    danger: true,
  }))) return;
  try {
    await store.dispatch('labels/delete', lbl.id);
  } catch (e) {
    useAlert('Failed to delete: ' + (e?.message ?? e));
  }
};

</script>

<template>
  <div class="fs-root">
    <!-- Mobile: back to the Settings menu, sat in a left gutter beside the
         section title (scrolls with the content, not floating up top). -->
    <button
      v-if="isMobile && view.startsWith('settings-')"
      class="fs-back-float" type="button" aria-label="Back to Settings"
      @click="$emit('back')">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
    </button>

    <!-- =================================================================
         INBOXES (Chat-Accounts) — table with add/edit/delete
         ================================================================= -->
    <template v-if="view === 'settings-inboxes'">
      <!-- Header -->
      <header class="fs-page-head">
        <div class="fs-page-titles">
          <h1 class="fs-h1">Chat-Accounts</h1>
          <p class="fs-page-desc">
            Connect Telegram and WhatsApp accounts that your agents will chat through,
            link them with the corresponding Fangate account to enable PPV, and
            optionally add a bot that replies automatically.
          </p>
          <!-- Fangate signup call-out — registration code is mandatory for the link to work -->
          <p class="fs-fangate-note">
            <strong class="fg-imp">Important:</strong>
            If you don't have a Fangate account, register one
            <a href="https://app.fangate.info/enter_email" target="_blank" rel="noopener">here</a>.
            Use <code class="fg-code">CODE: 5347z</code>
            when registering — otherwise your connection won't work.
          </p>
        </div>
        <button class="fs-btn-primary" @click="goAddAccount">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5"  y1="12" x2="19" y2="12"/>
          </svg>
          Add account
        </button>
      </header>

      <div class="fs-body">

        <!-- Sort control for the Chat-Accounts table -->
        <div class="fs-table-toolbar">
          <div class="fs-sort-wrap">
            <button class="fs-sort-btn" @click.stop="accSortOpen = !accSortOpen"
                    :title="'Sort accounts — ' + accSortLabel">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="15" y2="12"/>
                <line x1="3" y1="18" x2="9" y2="18"/>
                <polyline points="17 15 21 18 17 21"/>
              </svg>
              <span>{{ accSortLabel }}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            <div v-if="accSortOpen" class="fs-sort-menu" @click.stop>
              <button v-for="opt in ACC_SORT_OPTIONS" :key="opt.id"
                      class="fs-sort-opt" :class="{ active: accSort === opt.id }"
                      @click="setAccSort(opt.id)">
                <span class="fs-sort-radio" :class="{ checked: accSort === opt.id }"></span>
                {{ opt.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="fs-table">
          <div class="fs-table-head">
            <div>Status</div>
            <div>Account</div>
            <div>Type</div>
            <div>Agents</div>
            <div>Webhook</div>
            <div>Fangate</div>
            <div></div>
          </div>

          <div v-if="!inboxes.length" class="fs-empty">
            No accounts configured yet — hit "Add account" to connect a Telegram or WhatsApp inbox.
          </div>

          <!-- Phase C-3: per-inbox health banner. Stacks above the table so a
               multi-issue admin sees them all at once. Severity drives color. -->
          <div v-for="inbox in inboxesWithHealthIssue" :key="'health-banner-' + inbox.id"
               class="fs-health-banner"
               :class="'fs-health-banner--' + healthMeta(inbox).severity">
            <div class="fs-health-banner-body">
              <strong class="fs-health-banner-name">{{ inbox.name }}</strong>
              <span class="fs-health-banner-text">{{ healthMeta(inbox).bannerText }}</span>
            </div>
            <a v-if="healthMeta(inbox).recoveryUrl"
               :href="healthMeta(inbox).recoveryUrl"
               target="_blank" rel="noopener"
               class="fs-health-banner-cta">
              {{ healthMeta(inbox).recoveryLabel }} ↗
            </a>
          </div>

          <div v-for="inbox in sortedInboxes" :key="inbox.id"
               class="fs-table-row"
               :class="{ 'is-inactive': !isActive(inbox) }">

            <!-- Status cell (column 1) — status pill (top) + optional recovery
                 action pill (bottom). For MTProto-Enduser inboxes always shows
                 a pill (Healthy / Paused / Disconnected / Banned); for Bot /
                 Personal inboxes there's no health concept, so this stays
                 empty. The full text + banner above the table give context. -->
            <div class="fs-cell-status">
              <span v-if="healthMeta(inbox)"
                    class="fs-pill fs-health-pill"
                    :class="healthMeta(inbox).dotClass"
                    :title="healthMeta(inbox).bannerText">
                {{ healthMeta(inbox).label }}
              </span>
              <a v-if="healthMeta(inbox)?.recoveryUrl"
                 :href="healthMeta(inbox).recoveryUrl"
                 target="_blank" rel="noopener"
                 class="fs-pill fs-acc-action-pill">
                {{ healthMeta(inbox).recoveryLabel }} ↗
              </a>
            </div>

            <!-- Account cell (column 2) — platform icon + name. -->
            <div class="fs-cell-account">
              <div class="fs-acc-name">
                <svg v-if="platformOf(inbox) === 'telegram'" class="fs-acc-platform-icon"
                     width="16" height="16" viewBox="0 0 24 24" fill="#229ED9">
                  <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
                </svg>
                <svg v-else-if="platformOf(inbox) === 'whatsapp'" class="fs-acc-platform-icon"
                     width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span class="fs-acc-name-text">{{ inbox.name }}</span>
                <span v-if="!isActive(inbox)" class="fs-pill fs-pill--inactive">Inactive</span>
              </div>
            </div>

            <!-- Type — Bot / Enduser (Telegram Premium) / Enduser, from the
                 account-type chosen at creation (editable in the Edit modal). -->
            <div class="fs-cell-type">
              <span class="fs-pill fs-pill--user">{{ accountTypeMeta(inbox).icon }} {{ accountTypeMeta(inbox).label }}</span>
            </div>

            <!-- Agents (click cell → open shared agent picker) -->
            <div class="fs-cell-agents" @click="openAgentPicker($event, inbox)">
              <template v-if="assignedAgents(inbox).length">
                <span v-for="a in assignedAgents(inbox)" :key="a.id"
                      class="fs-pill fs-pill--agent">
                  👤 {{ a.name }}
                </span>
              </template>
              <span v-else class="fs-muted-italic">Unassigned</span>
            </div>

            <!-- Webhook (Live-Sales) — green once a signing secret is saved, else
                 a red "not set up" nudge. Click → open the row to set it up. -->
            <div class="fs-cell-webhook">
              <span v-if="webhookState[inbox.id] === 'errors'" class="fs-pill fs-pill--orange"
                    title="The Live-Sales webhook is getting deliveries but they're failing — check the signing secret you saved against the one in Fangate."
                    @click.stop="goEditInbox(inbox)" style="cursor:pointer;">
                Receiving errors
              </span>
              <span v-else-if="webhookState[inbox.id] === 'connected'" class="fs-pill fs-pill--linked"
                    title="Live-Sales webhook configured and receiving.">
                <span class="fs-link-dot"></span>connected
              </span>
              <span v-else class="fs-pill fs-pill--rejected"
                    title="No webhook yet — open the row to register it for sales tracking."
                    @click.stop="goEditInbox(inbox)" style="cursor:pointer;">
                not connected
              </span>
            </div>

            <!-- Fangate link — live status from /api/products validation -->
            <div class="fs-cell-fangate">
              <span v-if="linkStatus[inbox.id] === 'linked'"
                    class="fs-pill fs-pill--linked"
                    :title="linkEmail[inbox.id] ? ('Linked vault: ' + linkEmail[inbox.id]) : 'Token validates against Fangate'">
                <span class="fs-link-dot"></span><span class="fs-link-email">{{ linkEmail[inbox.id] || 'Linked' }}</span>
              </span>
              <span v-else-if="linkStatus[inbox.id] === 'checking'"
                    class="fs-muted-italic" title="Validating stored token…">
                Checking…
              </span>
              <span v-else-if="linkStatus[inbox.id] === 'rejected'"
                    class="fs-pill fs-pill--rejected"
                    title="Stored token was rejected by Fangate — open the row to re-paste."
                    @click.stop="goEditInbox(inbox)" style="cursor:pointer;">
                Token invalid
              </span>
              <span v-else class="fs-muted-italic">Not linked</span>
            </div>

            <!-- Kebab menu -->
            <div class="fs-cell-actions">
              <button class="fs-kebab" title="Actions"
                      @click.stop="toggleMenu($event, inbox.id)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="5"  cy="12" r="1.5"/>
                  <circle cx="12" cy="12" r="1.5"/>
                  <circle cx="19" cy="12" r="1.5"/>
                </svg>
              </button>
              <div v-if="openMenuId === inbox.id" class="fs-kebab-menu" @click.stop>
                <button class="fs-menu-item" @click="goEditInbox(inbox)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                  </svg>
                  Edit account
                </button>
                <button class="fs-menu-item" @click="openMenuId = null; openAgentPicker($event, inbox)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Assign agents
                </button>
                <button class="fs-menu-item"
                        :class="{ 'is-warn': isActive(inbox), 'is-ok': !isActive(inbox) }"
                        @click="toggleActive(inbox)">
                  <svg v-if="!isActive(inbox)" width="13" height="13" viewBox="0 0 24 24"
                       fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <svg v-else width="13" height="13" viewBox="0 0 24 24"
                       fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <rect x="6"  y="4" width="4" height="16"/>
                    <rect x="14" y="4" width="4" height="16"/>
                  </svg>
                  {{ isActive(inbox) ? 'Deactivate' : 'Reactivate' }}
                </button>
                <div class="fs-menu-sep"></div>
                <button class="fs-menu-item is-danger" @click="goDeleteInbox(inbox)">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                  </svg>
                  Delete account
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Collapsible API-token help -->
        <details class="fs-help" :open="helpOpen" @toggle="helpOpen = $event.target.open">
          <summary class="fs-help-summary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="var(--accent-strong)" stroke-width="2" stroke-linecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>How do I get a Telegram or Fangate API token?</span>
            <svg class="fs-help-chev" width="14" height="14" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </summary>

          <div class="fs-help-body">
            <!-- Telegram -->
            <div class="fs-help-section">
              <div class="fs-help-platform">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#229ED9">
                  <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
                </svg>
                <span>Telegram</span>
              </div>
              <div class="fs-help-subtitle">Bot-Account</div>
              <ol class="fs-help-list">
                <li>Open <a href="https://t.me/BotFather" target="_blank" rel="noopener">@BotFather</a> in Telegram</li>
                <li>Send <code>/newbot</code> and follow the instructions</li>
                <li>Copy the HTTP API token BotFather sends back and paste it when linking</li>
              </ol>
              <div class="fs-help-subtitle">Enduser Account (via phone number)</div>
              <ol class="fs-help-list">
                <li><strong>No token needed</strong> — log in with your phone number; Telegram sends a code to confirm. No Premium, no bot.</li>
              </ol>
              <div class="fs-help-subtitle">Personal-Account (Telegram Premium)</div>
              <ol class="fs-help-list">
                <li>Create a bot in <a href="https://t.me/BotFather" target="_blank" rel="noopener">@BotFather</a> (<code>/newbot</code>) — that's your token</li>
                <li>Turn on Secretary Mode: <code>/mybots</code> → your bot → Bot Settings → Secretary Mode</li>
                <li>In Telegram (Premium): Settings → Telegram Business → Chatbots → add the bot's @username, then paste the token here</li>
              </ol>
            </div>

            <div class="fs-help-divider"></div>

            <!-- Fangate -->
            <div class="fs-help-section">
              <div class="fs-help-platform">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <defs>
                    <linearGradient id="fg-help-grad" x1="0" y1="0" x2="24" y2="24">
                      <stop offset="0%"   stop-color="#1a0a6b"/>
                      <stop offset="100%" stop-color="#7040e0"/>
                    </linearGradient>
                  </defs>
                  <path d="M13 2 L4 14 L11 14 L11 22 L20 10 L13 10 Z" fill="url(#fg-help-grad)"/>
                </svg>
                <span>Fangate</span>
              </div>
              <ol class="fs-help-list">
                <li>Create an account at <a href="https://app.fangate.info/enter_email" target="_blank" rel="noopener" class="fg-link">app.fangate.info</a></li>
                <li><strong>Important:</strong> Enter the Creator-Chat code <code>5347z</code> during registration</li>
                <li>Verify your account and start your first upload</li>
                <li>Go to <strong>Settings → API Token</strong> and create a new token</li>
                <li>Paste the token here when linking the account</li>
              </ol>
            </div>
          </div>
        </details>

        <!-- Collapsible: webhook help -->
        <details class="fs-help" :open="webhookHelpOpen" @toggle="webhookHelpOpen = $event.target.open">
          <summary class="fs-help-summary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="var(--accent-strong)" stroke-width="2" stroke-linecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>How do I get and register a webhook, and what does it do?</span>
            <svg class="fs-help-chev" width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </summary>
          <div class="fs-help-body">
            <ol class="fs-help-list">
              <li>It receives your Fangate sales in real time, so every purchase shows up live in the matching chat and feeds the sales reports.</li>
              <li>Open a chat-account's <strong>Edit</strong>, connect its Fangate vault, then copy the <strong>Webhook URL</strong> and register it in Fangate under <strong>app.fangate.info → Settings → API</strong>. If Fangate shows a signing secret, paste it back into the Edit panel.</li>
              <li>Once set, you get <strong>live sale tracking, notifications and reports</strong> for that account — without it, sales can't be tracked.</li>
            </ol>
          </div>
        </details>

        <details class="fs-help" :open="botLimitsOpen" @toggle="botLimitsOpen = $event.target.open">
          <summary class="fs-help-summary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="var(--accent-strong)" stroke-width="2" stroke-linecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>What limitations do bots and access via Telegram Premium (Bot) have?</span>
            <svg class="fs-help-chev" width="14" height="14" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </summary>

          <div class="fs-help-body">
            <p class="fs-help-intro">
              Telegram enforces several rules on what a bot &mdash; and a Premium-Business
              connection that piggybacks on a bot &mdash; can do. Knowing them upfront saves
              hours of "why isn't this working" later.
            </p>

            <!-- Bot limitations -->
            <div class="fs-help-section">
              <div class="fs-help-platform">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#229ED9">
                  <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
                </svg>
                <span>Bots (standard Telegram Bot API)</span>
              </div>
              <ul class="fs-help-list">
                <li><strong>Bots cannot start conversations.</strong> A user must write to the bot first (or click <em>Start</em>) before the bot can send them anything &mdash; including the first PPV link.</li>
                <li><strong>Channels:</strong> the bot must be an <strong>admin</strong> of the channel to read posts and to publish on the channel's behalf. Plain members can't post in broadcast channels at all.</li>
                <li><strong>Groups &amp; supergroups (Privacy Mode):</strong> by default a bot only sees messages that <em>mention it</em>, are a <em>reply to one of its messages</em>, or start with a <em>/command</em>. To read every message, either promote the bot to admin <em>or</em> disable Privacy Mode via <a href="https://t.me/BotFather" target="_blank" rel="noopener">@BotFather</a> (<code>/setprivacy</code> &rarr; <em>Disable</em>).</li>
                <li><strong>No history before the bot joined.</strong> A bot only ever sees messages sent <em>after</em> it became a member &mdash; old conversations stay invisible.</li>
                <li><strong>Rate limits.</strong> Telegram caps bots at roughly 30 messages/second across all chats, 1 message/second to the same private chat, and 20 messages/minute to the same group.</li>
                <li><strong>File size:</strong> the Bot API only lets the bot download files up to <strong>20&nbsp;MB</strong> and upload up to <strong>50&nbsp;MB</strong>. Larger media has to be sent from the Telegram app directly.</li>
                <li><strong>Profile photos may be unavailable.</strong> For some contacts the bot can't pull the avatar &mdash; we then fall back to initials. Not a bug, just a Telegram limit.</li>
              </ul>
            </div>

            <div class="fs-help-divider"></div>

            <!-- Premium-Business limitations -->
            <div class="fs-help-section">
              <div class="fs-help-platform">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#5865F2">
                  <path d="M12 2L9.6 8.4L3 9.5L7.5 14.3L6.4 21L12 17.8L17.6 21L16.5 14.3L21 9.5L14.4 8.4L12 2Z"/>
                </svg>
                <span>Premium-Business connection</span>
              </div>
              <ul class="fs-help-list">
                <li><strong>The creator needs Telegram Premium.</strong> Without an active Premium subscription the Business connection can't be created at all.</li>
                <li><strong>Per-creator setup.</strong> The connection must be added manually on the creator's phone (<em>Settings &rarr; Telegram Business &rarr; Chatbots</em>) for every Telegram account that should run through Creator-Chat.</li>
                <li><strong>Direct chats only.</strong> Premium-Business covers 1:1 conversations &mdash; the bot can't post to groups or channels through this connection. For those, use the bot-admin path described above.</li>
                <li><strong>Messages typed by the creator on their phone</strong> are mirrored into Creator-Chat as outgoing (with a <em>U</em> badge so you can tell them apart). Useful to stay in sync, but it also means messages can land here a few seconds delayed.</li>
                <li><strong>Some media can't be downloaded.</strong> Attachments inside Premium-Business chats are sometimes blocked from bot download (Telegram limit). The message arrives with a note &mdash; the creator can still view it on their phone.</li>
                <li><strong>Voice messages</strong> as outgoing memos are fully supported &mdash; OGG/Opus, MP3 and M4A files are sent as Telegram voice notes (round microphone bubble).</li>
                <li><strong>Token rotation.</strong> If the underlying bot's token is rotated in BotFather, the Premium-Business connection drops. Re-link the account here and the connection is restored.</li>
              </ul>
            </div>
          </div>
        </details>
      </div>

      <!-- =================================================================
           EDIT INBOX MODAL — real, calls inboxes/updateInbox
           ================================================================= -->
      <div v-if="editingInbox" class="fs-modal-overlay" @click.self="closeAllModals">
        <div class="fs-modal">
          <div class="fs-modal-head">
            <h3>Edit chat-account</h3>
            <button class="fs-modal-close" @click="closeAllModals">×</button>
          </div>
          <div class="fs-modal-body">
            <div class="fs-form-row">
              <div class="fs-form-avatar">
                <img v-if="editingInbox.avatar_url"
                     :src="editingInbox.avatar_url"
                     :alt="editingInbox.name" />
                <span v-else>{{ getInitials(editingInbox.name) }}</span>
              </div>
              <div class="fs-form-acc-info">
                <div class="fs-form-acc-platform">{{ platformLabel(editingInbox) }}</div>
                <!-- Show the account NAME as the primary identity line. Many
                     Telegram accounts have no @username/phone/email, so the old
                     handleOf() rendered just "—" and the name was invisible. -->
                <div class="fs-form-acc-handle">{{ editingInbox.name || handleOf(editingInbox) }}</div>
              </div>
            </div>

            <!-- Telegram-only: one-time business-owner mapping hint. Incoming
                 chats only mirror into the account's OWN inbox once we know who
                 owns it, and that mapping is learned from an OUTGOING business
                 message the owner sends from their phone to a real human. -->
            <div v-if="platformOf(editingInbox) === 'telegram'" class="fs-info-callout">
              <strong>One-time Telegram setup:</strong> For incoming chats to mirror into
              this account's own inbox, the account owner should send
              <strong>at least one message from their phone</strong> to a real Telegram
              contact (not a bot) after connecting. That's how Creator-Chat learns who
              owns the account. Until then messages still arrive — just only in the bot's inbox.
            </div>

            <label class="fs-field">
              <span class="fs-field-label">Display name</span>
              <input v-model="editForm.name" type="text"
                     class="fs-field-input" placeholder="Account name" />
            </label>

            <label class="fs-field">
              <span class="fs-field-label">Account type</span>
              <!-- Channel::TelegramUser inboxes (MTProto/Enduser) are typed by
                   the channel class itself — there's no account_type column to
                   change, so a dropdown would either be a no-op or actively
                   misleading. Render the fixed label instead. -->
              <div v-if="editingInbox?.channel_type === 'Channel::TelegramUser'"
                   class="fs-field-input fs-field-readonly">
                👤 Enduser
              </div>
              <select v-else v-model="editForm.type" class="fs-field-input">
                <option value="bot">Bot</option>
                <option value="personal">Enduser (Telegram Premium)</option>
              </select>
            </label>

            <label class="fs-field fs-field-toggle">
              <input v-model="editForm.enable_auto_assignment" type="checkbox" />
              <span class="fs-toggle-label">
                <strong>Active</strong>
                <small>When off, no new messages can be sent or received.</small>
              </span>
            </label>

            <!-- Fangate vault: per-chat-account API token -->
            <div class="fs-fangate-section">
              <div class="fs-fangate-head">
                <div class="fs-fangate-title">Fangate vault</div>
                <span v-if="fangateForm.connected" class="fs-fangate-pill">
                  <span class="fs-fangate-dot"></span>
                  Connected
                </span>
                <span v-else class="fs-fangate-pill fs-fangate-pill--off">
                  Not connected
                </span>
              </div>
              <p class="fs-fangate-desc">
                Paste the Fangate API token to load this chat-account's vault
                (products, sales, wallet) into the side panel. Create one under
                <a href="https://app.fangate.info" target="_blank" rel="noopener">app.fangate.info</a>
                → Settings → API. When creating, tick
                <strong>Can view transactions</strong> (needed for the Sales tab)
                and <strong>Can create new URLs</strong> (needed for Upload &amp;
                Edit-price). Read-only product listing is always on.
                See <a href="https://docs.fangate.app/api-reference/api-keys.html"
                       target="_blank" rel="noopener">docs.fangate.app — API keys</a>
                for the full abilities matrix.
                <strong>Don't share this token in plaintext</strong> — anyone with
                it can call the API as you.
              </p>

              <label class="fs-field">
                <span class="fs-field-label">Fangate email <span style="color:#e5484d;font-weight:800;">*</span></span>
                <input v-model="fangateForm.email" type="email"
                       class="fs-field-input"
                       placeholder="e.g. leonie@fangate.app"
                       autocomplete="off" />
              </label>
              <p class="fs-fangate-desc">
                Use the email you log in to Fangate with — this lets you track
                this account's sales and get live and in-chat notifications of
                successful sales.
              </p>

              <label class="fs-field">
                <span class="fs-field-label">API token</span>
                <input v-model="fangateForm.token" type="password"
                       class="fs-field-input"
                       :placeholder="fangateForm.connected ? 'Leave empty to keep current token' : 'id|secret-token'"
                       autocomplete="off"
                       @keydown.enter.prevent="saveFangateToken" />
              </label>

              <div v-if="fangateForm.error" class="fs-form-error">{{ fangateForm.error }}</div>

              <div class="fs-fangate-actions">
                <button v-if="fangateForm.connected"
                        class="fs-btn-secondary fs-btn-danger-ghost"
                        type="button"
                        @click="disconnectFangate">
                  Disconnect
                </button>
                <button class="fs-btn-primary"
                        type="button"
                        :disabled="fangateForm.saving || !fangateForm.token.trim() || !fangateForm.email.trim()"
                        @click="saveFangateToken">
                  {{ fangateForm.saving
                       ? 'Verifying…'
                       : (fangateForm.connected ? 'Replace token' : 'Connect') }}
                </button>
              </div>

              <!-- Live-Sales webhook: paste this URL into Fangate → "Register webhook".
                   Shown for any chat-account (independent of the content vault token). -->
              <div v-if="fangateForm.webhookUrl" class="fs-webhook-block">
                <div class="fs-webhook-head">Live-Sales webhook</div>
                <p class="fs-fangate-hint">
                  <strong>Must-have for sales tracking.</strong> Register this URL in Fangate under
                  <strong>app.fangate.info → Settings → API</strong> (keep the payment events on).
                  Only this enables live sale tracking, notifications and reports in this chat.
                </p>
                <label class="fs-field">
                  <span class="fs-field-label">Webhook URL</span>
                  <div class="fs-webhook-row">
                    <input :value="fangateForm.webhookUrl" type="text" readonly
                           class="fs-field-input" @focus="$event.target.select()" />
                    <button class="fs-btn-secondary" type="button" @click="copyWebhookUrl">
                      {{ fangateForm.webhookCopied ? 'Copied' : 'Copy' }}
                    </button>
                  </div>
                </label>
                <label class="fs-field">
                  <span class="fs-field-label">
                    Signing secret
                    <span class="fs-webhook-state">{{ fangateForm.webhookSecretSet ? '· set' : '· optional' }}</span>
                  </span>
                  <div class="fs-webhook-row">
                    <input v-model="fangateForm.webhookSecret" type="password"
                           class="fs-field-input"
                           :placeholder="fangateForm.webhookSecretSet ? 'Leave empty to keep current secret' : 'Paste the secret Fangate shows after Register'"
                           autocomplete="off"
                           @keydown.enter.prevent="saveWebhookSecret" />
                    <button class="fs-btn-primary" type="button"
                            :disabled="fangateForm.savingSecret || !fangateForm.webhookSecret.trim()"
                            @click="saveWebhookSecret">
                      {{ fangateForm.savingSecret ? 'Saving…' : 'Save' }}
                    </button>
                    <button v-if="fangateForm.webhookSecretSet"
                            class="fs-btn-secondary fs-btn-danger-ghost" type="button"
                            :disabled="fangateForm.savingSecret"
                            @click="removeWebhookSecret">
                      Remove
                    </button>
                  </div>
                </label>
                <p class="fs-fangate-hint">
                  If Fangate shows a signing secret when you register, paste it here for verified
                  (HMAC) deliveries. If not, leave it empty — the unique URL above is the credential.
                </p>
              </div>
            </div>

            <div v-if="saveError" class="fs-form-error">{{ saveError }}</div>
          </div>
          <div class="fs-modal-foot fs-modal-foot--split">
            <div style="display:flex;gap:8px;">
              <button class="fs-btn-danger"
                      @click="closeAllModals(); goDeleteInbox(editingInbox)">
                Delete account
              </button>
              <!-- Reconnect: always available for Enduser inboxes; rendered as the
                   primary (prominent) action when the session is broken. Re-auths
                   in place, keeping the inbox's chats. -->
              <button v-if="editingInbox?.channel_type === 'Channel::TelegramUser'"
                      type="button"
                      :class="['unauthorized','disconnected'].includes(editingInbox?.health_status) ? 'fs-btn-primary' : 'fs-btn-secondary'"
                      @click="goReconnect(editingInbox)">
                Reconnect
              </button>
            </div>
            <div style="display:flex;gap:8px;">
              <button class="fs-btn-secondary" @click="closeAllModals">Cancel</button>
              <button class="fs-btn-primary"
                      :disabled="isSaving || !editForm.name.trim()"
                      @click="saveInbox">
                {{ isSaving ? 'Saving…' : 'Save changes' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- =================================================================
         BOTS & AI — bots table + ChatGPT card + help (prototype parity)
         ================================================================= -->
    <template v-else-if="view === 'settings-bots'">
      <!-- ===== BOTS section ===== -->
      <header class="fs-page-head">
        <div class="fs-page-titles">
          <h1 class="fs-h1">Automations</h1>
          <p class="fs-page-desc">
            Agent bots automatically react to keywords, handle first contact, or
            answer FAQs — and can hand off to humans when needed.
          </p>
        </div>
        <button class="fs-btn-primary" @click="openBotModal()">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5"  y1="12" x2="19" y2="12"/>
          </svg>
          Add new bot
        </button>
      </header>

      <div class="fs-body">

        <!-- Bots table -->
        <div class="fs-table">
          <!-- Account is the parent row (expandable); bots are children with
               their own Bot-name column. Five columns: Account/Bot · Mode ·
               Keywords · Status · (actions). Linked-Accounts column dropped —
               it's now implicit in the account header. -->
          <div class="fs-table-head fs-bot-grid">
            <div>Account / Bot</div>
            <div>Mode</div>
            <div>Keywords</div>
            <div>Actions</div>
            <div>Status</div>
            <div></div>
          </div>

          <div v-if="!bots.length" class="fs-empty">
            No bots configured yet — hit "Add new bot" to build your first
            keyword-driven auto-reply flow.
          </div>

          <template v-for="group in botsByInbox" :key="'g-' + group.key">
            <!-- Account header (clickable parent row). Click toggles expand. -->
            <div class="fs-bot-account-row"
                 :class="{ 'is-collapsed': !isAccountExpanded(group.key) }"
                 @click="toggleAccountExpand(group.key)">
              <span class="fs-bot-account-chev"
                    :class="{ 'is-open': isAccountExpanded(group.key) }">▶</span>
              <span class="fs-bot-account-name">{{ group.name }}</span>
              <span class="fs-bot-account-count">
                {{ group.items.length }} bot{{ group.items.length === 1 ? '' : 's' }}
              </span>
            </div>

            <!-- Child bot rows (hidden when account is collapsed) -->
            <template v-if="isAccountExpanded(group.key)">
              <div v-for="b in group.items" :key="b.id"
                   class="fs-table-row fs-bot-grid fs-bot-child-row"
                   @click="editBot(b)">
                <div class="fs-cell-account">
                  <div class="fs-bot-icon">🤖</div>
                  <div class="fs-acc-text">
                    <div class="fs-acc-name">{{ b.name }}</div>
                    <div class="fs-acc-handle">{{ b.handle }}</div>
                  </div>
                </div>
                <div>
                  <span class="fs-pill">
                    {{ b.scopeIcon }} {{ b.scopeLabel }}
                  </span>
                </div>
                <div class="fs-bot-keywords">
                  <span v-for="kw in (b.rules || [])" :key="kw" class="fs-bot-kw-chip">{{ kw }}</span>
                  <span v-if="!b.rules?.length" class="fs-muted-italic">— none —</span>
                </div>
                <!-- Action overview — just type+icon chips, not param details.
                     Reuses each ACTION_DEFS entry's icon + label so adding a
                     new action type later automatically shows up here. -->
                <div class="fs-bot-actions-col">
                  <span v-for="(a, idx) in (b.actionSummary || [])"
                        :key="a.type + idx"
                        class="fs-bot-action-chip"
                        :title="a.label">
                    <span class="fs-bot-action-chip-icon">{{ a.icon }}</span>
                    <span class="fs-bot-action-chip-label">{{ a.label }}</span>
                  </span>
                  <span v-if="!b.actionSummary?.length" class="fs-muted-italic">— none —</span>
                </div>
                <div>
                  <span class="fs-pill" :class="b.active ? 'fs-pill--status-on' : 'fs-pill--status-off'">
                    <span class="fs-status-dot" :class="b.active ? 'fs-status-dot--on' : 'fs-status-dot--off'"></span>
                    {{ b.active ? 'Active' : 'Off' }}
                  </span>
                </div>
                <div class="fs-cell-actions" @click.stop>
                  <button class="fs-kebab" title="Edit" @click="editBot(b)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <path d="M12 20h9"/>
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                    </svg>
                  </button>
                  <button class="fs-kebab" title="Delete" style="color:#b91c1c;" @click="deleteBot(b)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                    </svg>
                  </button>
                </div>
              </div>
            </template>
          </template>
        </div>

        <!-- "How do bots work?" help -->
        <details class="fs-help" :open="botsHelpOpen" @toggle="botsHelpOpen = $event.target.open">
          <summary class="fs-help-summary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="var(--accent-strong)" stroke-width="2" stroke-linecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>How do bots work?</span>
            <svg class="fs-help-chev" width="14" height="14" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </summary>
          <div class="fs-help-body">
            <p class="fs-help-p">
              For each bot you configure <strong>trigger rules</strong>: keyword/phrase → action
              (auto-reply, Fangate link, label, assign agent, handoff).
            </p>
          </div>
        </details>

        <!-- ===== CHATGPT section ===== -->
        <div class="fs-section-head">
          <div class="fs-page-titles">
            <h2 class="fs-h2">
              ChatGPT
              <span v-if="chatgptComingSoon" class="fs-coming-soon-pill">Coming soon</span>
            </h2>
            <p class="fs-page-desc">
              Connect your OpenAI API key to power the <strong>Suggest reply</strong> button in chat.
              It generates a contextual reply suggestion from the conversation history that the agent can edit before sending.
              <em v-if="chatgptComingSoon" class="fs-coming-soon-note">
                — The integration is being wired up; the button below will activate as soon as it lands.
              </em>
            </p>
          </div>
          <button v-if="aiState.connected && !chatgptComingSoon" class="fs-btn-danger" @click="disconnectAI">
            Disconnect
          </button>
          <button v-else
                  class="fs-btn-primary"
                  :disabled="chatgptComingSoon"
                  :title="chatgptComingSoon ? 'Coming soon — backend wiring in progress' : 'Connect your OpenAI key'"
                  @click="!chatgptComingSoon && openAIConnect()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5"  y1="12" x2="19" y2="12"/>
            </svg>
            Connect ChatGPT
          </button>
        </div>

        <div class="fs-table" :class="{ 'is-coming-soon': chatgptComingSoon }">
          <div class="fs-table-head fs-ai-grid">
            <div>Provider</div>
            <div>API Key</div>
            <div>Status</div>
            <div></div>
          </div>
          <div class="fs-table-row fs-ai-grid"
               :style="!aiState.connected ? 'opacity:0.55;' : ''">
            <div class="fs-cell-account">
              <div class="fs-openai-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494z"/>
                </svg>
              </div>
              <div class="fs-acc-text">
                <div class="fs-acc-name">OpenAI</div>
                <div class="fs-acc-handle">ChatGPT</div>
              </div>
            </div>
            <div style="font-family:'JetBrains Mono','Inter',sans-serif;font-size:11px;color:var(--ink-medium);">
              <template v-if="aiState.connected">{{ aiState.keyMasked }}</template>
              <span v-else class="fs-muted-italic">— not set —</span>
            </div>
            <div>
              <span v-if="!aiState.connected" class="fs-pill">
                <span class="fs-status-dot fs-status-dot--off"></span>Inactive
              </span>
              <span v-else-if="aiState.active" class="fs-pill fs-pill--status-on">
                <span class="fs-status-dot fs-status-dot--on"></span>Active
              </span>
              <span v-else class="fs-pill fs-pill--status-paused">
                <span class="fs-status-dot fs-status-dot--paused"></span>Paused
              </span>
            </div>
            <div class="fs-cell-actions">
              <button v-if="aiState.connected" class="fs-kebab" @click="openAIConnect" title="Edit">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                </svg>
              </button>
              <button v-if="aiState.connected" class="fs-kebab"
                      :title="aiState.active ? 'Pause' : 'Activate'"
                      @click="toggleAIActive">
                <svg v-if="aiState.active" width="13" height="13" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="6" y="4" width="4" height="16"/>
                  <rect x="14" y="4" width="4" height="16"/>
                </svg>
                <svg v-else width="13" height="13" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- "How does ChatGPT work here?" help -->
        <details class="fs-help" :open="aiHelpOpen" @toggle="aiHelpOpen = $event.target.open">
          <summary class="fs-help-summary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="var(--accent-strong)" stroke-width="2" stroke-linecap="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>How does ChatGPT work here?</span>
            <svg class="fs-help-chev" width="14" height="14" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </summary>
          <div class="fs-help-body">
            <p class="fs-help-p">
              Once connected, the <strong>Suggest reply</strong> button appears in every chat's
              compose area when the field is empty. Click it to get a contextual reply suggestion;
              click the suggestion to drop it into the composer, then edit and send.
            </p>
            <ol class="fs-help-ol">
              <li>Get an API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener">platform.openai.com/api-keys</a></li>
              <li>Click <strong>Connect ChatGPT</strong> and paste your key (starts with <code>sk-…</code>)</li>
              <li>The key is stored encrypted on your backend (never kept in the browser). All API calls go through your server.</li>
            </ol>
            <p class="fs-help-p" style="margin-top:10px;">
              <strong>Privacy:</strong> Conversation snippets are sent to OpenAI to generate
              suggestions. Avoid connecting if you handle highly sensitive content without DPA approval.
            </p>
          </div>
        </details>
      </div>

      <!-- ===== Add / Edit Bot modal (real builder, wired to Chatwoot AutomationsAPI) ===== -->
      <div v-if="botModalOpen" class="fs-modal-overlay" @click.self="closeBotModal()">
        <div class="fs-modal fs-bot-modal">
          <div class="fs-modal-head">
            <h3 v-if="botModalMode === 'chooser'">Add new bot</h3>
            <h3 v-else-if="botModalMode === 'pick'">Use existing rule</h3>
            <h3 v-else>{{ botEditingId ? 'Edit bot' : 'Build new bot' }}</h3>
            <button class="fs-modal-close" @click="closeBotModal()">×</button>
          </div>

          <!-- ── Mode chooser ────────────────────────────────────────────── -->
          <div v-if="botModalMode === 'chooser'" class="fs-modal-body">
            <p class="fs-modal-text" style="margin:0 0 14px;">
              A Creator-Chat bot is a keyword-driven auto-reply tied to one of your
              Telegram inboxes. Pick how you want to start.
            </p>
            <div class="fs-bot-choice-grid">
              <button class="fs-bot-choice" @click="switchBotMode('create')">
                <div class="fs-bot-choice-emoji">➕</div>
                <div class="fs-bot-choice-title">Build new</div>
                <div class="fs-bot-choice-sub">
                  Create from scratch — name, keyword triggers, reply message.
                </div>
              </button>
              <button class="fs-bot-choice" :disabled="!bots.length" @click="switchBotMode('pick')">
                <div class="fs-bot-choice-emoji">🔗</div>
                <div class="fs-bot-choice-title">Use existing rule</div>
                <div class="fs-bot-choice-sub">
                  Edit a Chatwoot automation rule you already created.
                </div>
              </button>
            </div>
          </div>

          <!-- ── Pick existing rule (list) ───────────────────────────────── -->
          <div v-else-if="botModalMode === 'pick'" class="fs-modal-body">
            <div v-if="!bots.length" class="fs-empty">No existing rules yet.</div>
            <div v-else class="fs-bot-pick-list">
              <button v-for="b in bots" :key="b.id"
                      class="fs-bot-pick-row"
                      @click="editBot(b)">
                <div class="fs-bot-icon">🤖</div>
                <div style="flex:1; min-width:0;">
                  <div class="fs-acc-name">{{ b.name }}</div>
                  <div class="fs-acc-handle">{{ b.handle }}</div>
                </div>
                <span class="fs-pill" :class="b.active ? 'fs-pill--status-on' : 'fs-pill--status-off'">
                  <span class="fs-status-dot" :class="b.active ? 'fs-status-dot--on' : 'fs-status-dot--off'"></span>
                  {{ b.active ? 'Active' : 'Off' }}
                </span>
              </button>
            </div>
            <button class="fs-bot-back" @click="switchBotMode('chooser')">‹ Back</button>
          </div>

          <!-- ── Builder form (create OR edit) ───────────────────────────── -->
          <div v-else class="fs-modal-body">
            <label class="fs-field">
              <span class="fs-field-label">Bot name</span>
              <input v-model="botForm.name" class="fs-field-input" placeholder="e.g. Greeter" />
            </label>

            <label class="fs-field">
              <span class="fs-field-label">Trigger event</span>
              <select v-model="botForm.event" class="fs-field-input">
                <option v-for="opt in BOT_EVENT_OPTIONS" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <span class="fs-field-hint">
                When the bot fires. Lifecycle events (new conversation,
                reopened, updated) skip the keyword field — they trigger once
                per conversation, not per message.
              </span>
            </label>

            <label class="fs-field">
              <span class="fs-field-label">Linked Telegram inbox</span>
              <select v-model="botForm.inboxId" class="fs-field-input">
                <option v-if="!botInboxOptions.length" :value="null" disabled>
                  No Telegram inboxes configured yet
                </option>
                <option v-for="ix in botInboxOptions" :key="ix.id" :value="ix.id">
                  {{ ix.name || `Inbox #${ix.id}` }}
                </option>
              </select>
              <span class="fs-field-hint">
                Bot only reacts inside this inbox's chats, groups and channels.
              </span>
            </label>

            <label class="fs-field">
              <span class="fs-field-label">Scope</span>
              <select v-model="botForm.scope" class="fs-field-input">
                <option v-for="opt in BOT_SCOPE_OPTIONS" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <span class="fs-field-hint">
                Narrows the bot to a chat kind. Backed by Telegram's <code>chat.type</code>
                — DMs ({{ '\'private\'' }}), Groups ({{ '\'group\' + \'supergroup\'' }}),
                or Channels (broadcast {{ '\'channel\'' }}).
              </span>
            </label>

            <label v-if="currentEventNeedsKeyword" class="fs-field">
              <span class="fs-field-label">Trigger keywords</span>
              <div class="fs-bot-kw-row">
                <input v-model="newKeywordInput"
                       class="fs-field-input"
                       placeholder="Type a keyword + Enter (e.g. ping)"
                       @keydown.enter.prevent="addBotKeyword" />
                <button type="button" class="fs-btn-secondary" @click="addBotKeyword">Add</button>
              </div>
              <div v-if="botForm.keywords.length" class="fs-bot-kw-pills">
                <span v-for="(kw, idx) in botForm.keywords" :key="idx" class="fs-bot-kw-pill">
                  {{ kw }}
                  <button type="button" class="fs-bot-kw-x"
                          @click="removeBotKeyword(idx)" title="Remove">×</button>
                </span>
              </div>
              <span class="fs-field-hint">
                Bot fires when an incoming message <strong>contains any</strong> of these.
              </span>
            </label>

            <!-- ── Actions editor ───────────────────────────────────────── -->
            <div class="fs-field">
              <span class="fs-field-label">Actions</span>
              <div class="fs-bot-actions-list">
                <div v-for="(a, idx) in botForm.actions" :key="a.uid"
                     class="fs-bot-action">
                  <!-- Action header: index, type-dropdown, reorder + remove -->
                  <div class="fs-bot-action-head">
                    <span class="fs-bot-action-idx">{{ idx + 1 }}</span>
                    <select :value="a.type"
                            class="fs-field-input fs-bot-action-type"
                            @change="changeBotActionType(idx, $event.target.value)">
                      <option v-for="def in ACTION_DEFS" :key="def.type" :value="def.type">
                        {{ def.icon }} {{ def.label }}
                      </option>
                      <option v-if="!ACTION_DEF_BY_TYPE[a.type]" :value="a.type">
                        ? {{ a.type }} (unknown — preserved)
                      </option>
                    </select>
                    <button type="button" class="fs-bot-action-move"
                            :disabled="idx === 0"
                            title="Move up"
                            @click="moveBotAction(idx, -1)">▲</button>
                    <button type="button" class="fs-bot-action-move"
                            :disabled="idx === botForm.actions.length - 1"
                            title="Move down"
                            @click="moveBotAction(idx, 1)">▼</button>
                    <button type="button" class="fs-bot-action-x"
                            title="Remove action"
                            @click="removeBotAction(idx)">×</button>
                  </div>

                  <!-- Per-type param controls. Each branch only renders if the
                       action definition declares that paramUI shape, so adding
                       a new action type later is a single ACTION_DEFS entry +
                       a v-if branch here. -->
                  <div class="fs-bot-action-body">
                    <p class="fs-bot-action-desc">{{ defForAction(a).desc }}</p>

                    <!-- textarea: send_message / add_private_note -->
                    <textarea v-if="defForAction(a).paramUI === 'textarea'"
                              v-model="a.params[defForAction(a).paramKey]"
                              class="fs-field-input"
                              rows="3"
                              :placeholder="a.type === 'send_message' ? 'e.g. pong 🏓' : 'Internal note text…'"></textarea>

                    <!-- text: send_webhook_event -->
                    <input v-else-if="defForAction(a).paramUI === 'text'"
                           v-model="a.params[defForAction(a).paramKey]"
                           type="text"
                           class="fs-field-input"
                           :placeholder="a.type === 'send_webhook_event' ? 'https://hooks.example.com/…' : ''" />

                    <!-- select: change_priority / change_status -->
                    <select v-else-if="defForAction(a).paramUI === 'select'"
                            v-model="a.params[defForAction(a).paramKey]"
                            class="fs-field-input">
                      <option v-for="opt in defForAction(a).options" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                      </option>
                    </select>

                    <!-- labels: multi-select from props.labels -->
                    <div v-else-if="defForAction(a).paramUI === 'labels'"
                         class="fs-bot-action-labels">
                      <div v-if="!labels.length" class="fs-muted-italic" style="font-size:11px;">
                        No labels configured — create some in Settings → Labels first.
                      </div>
                      <button v-for="lbl in labels" :key="lbl.id"
                              type="button"
                              class="fs-bot-action-lbl"
                              :class="{ 'is-on': (a.params.labels || []).includes(lbl.title) }"
                              :style="{
                                borderColor: lbl.color || 'var(--accent)',
                                background: (a.params.labels || []).includes(lbl.title)
                                  ? (lbl.color || 'var(--accent)')
                                  : 'transparent',
                                color: (a.params.labels || []).includes(lbl.title) ? '#fff' : (lbl.color || 'var(--accent-strong)'),
                              }"
                              @click="toggleActionLabel(a, lbl.title)">
                        {{ lbl.title }}
                      </button>
                    </div>

                    <!-- agent: single-select dropdown from props.agents -->
                    <select v-else-if="defForAction(a).paramUI === 'agent'"
                            v-model="a.params[defForAction(a).paramKey]"
                            class="fs-field-input">
                      <option :value="null" disabled>— Pick an agent —</option>
                      <option v-for="ag in agents" :key="ag.id" :value="ag.id">
                        {{ ag.name || ag.email }}
                      </option>
                    </select>

                    <!-- inviteLink: composite — target chat_id + member_limit
                         + expire_seconds + message template. Bot must be admin
                         in the target chat with can_invite_users. -->
                    <div v-else-if="defForAction(a).paramUI === 'inviteLink'"
                         class="fs-bot-invite-grid">
                      <label class="fs-bot-invite-field">
                        <span>Target chat_id</span>
                        <input v-model="a.params.chatId" type="text"
                               class="fs-field-input"
                               placeholder="-1001234567890" />
                        <small>Negative number for groups/channels — find it via @userinfobot or in the chat URL.</small>
                      </label>
                      <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
                        <label class="fs-bot-invite-field">
                          <span>Member limit</span>
                          <input v-model.number="a.params.memberLimit" type="number" min="1" max="99999"
                                 class="fs-field-input" placeholder="1" />
                          <small>Max joins through this link (1 = single-use).</small>
                        </label>
                        <label class="fs-bot-invite-field">
                          <span>Expires in (seconds)</span>
                          <input v-model.number="a.params.expireSeconds" type="number" min="0" max="2592000"
                                 class="fs-field-input" placeholder="3600" />
                          <small>0 = never. 3600 = 1h, 86400 = 24h, 604800 = 7d.</small>
                        </label>
                      </div>
                      <label class="fs-bot-invite-field">
                        <span>Message template</span>
                        <textarea v-model="a.params.template" class="fs-field-input" rows="2"
                                  :placeholder="inviteLinkPlaceholder"></textarea>
                        <small>Sent to the user — use <code>&#123;&#123;link&#125;&#125;</code> as placeholder for the generated URL.</small>
                      </label>
                    </div>

                    <!-- none: render nothing for parameterless actions -->
                    <div v-else-if="defForAction(a).paramUI === 'none'"
                         class="fs-muted-italic" style="font-size:11px;">
                      No parameters — this action just runs.
                    </div>

                    <!-- unknown: surface the raw payload so the user knows it's preserved -->
                    <div v-else class="fs-muted-italic" style="font-size:11px;">
                      Unknown action <code>{{ a.type }}</code> — params will be saved unchanged.
                    </div>
                  </div>
                </div>
              </div>

              <!-- Add-action picker: dropdown + button. Keeps the bot
                   composable without forcing the user to learn all action
                   names up front. -->
              <div class="fs-bot-add-action">
                <select v-model="newActionType" class="fs-field-input">
                  <option v-for="def in ACTION_DEFS" :key="def.type" :value="def.type">
                    {{ def.icon }} {{ def.label }}
                  </option>
                </select>
                <button type="button" class="fs-btn-secondary"
                        @click="addBotAction(newActionType)">
                  + Add action
                </button>
              </div>
              <span class="fs-field-hint">
                Actions run top-to-bottom in order. Telegram-bound actions
                (Send reply) use the linked inbox's send path — incl.
                business_connection + mirror handling.
              </span>
            </div>

            <label class="fs-field" style="display:flex; align-items:center; gap:8px;">
              <input type="checkbox" v-model="botForm.active" style="width:auto;" />
              <span class="fs-field-label" style="margin:0;">Active</span>
            </label>

            <div v-if="botError" class="fs-form-error">{{ botError }}</div>
          </div>

          <!-- ── Footer ──────────────────────────────────────────────────── -->
          <div class="fs-modal-foot">
            <button class="fs-btn-secondary" @click="closeBotModal()">Cancel</button>
            <button v-if="botModalMode === 'create'"
                    class="fs-btn-primary"
                    :disabled="botSaving"
                    @click="saveBot">
              {{ botSaving ? 'Saving…' : (botEditingId ? 'Save changes' : 'Create bot') }}
            </button>
          </div>
        </div>
      </div>

      <!-- ===== ChatGPT connect modal ===== -->
      <div v-if="aiModalOpen" class="fs-modal-overlay" @click.self="closeAIConnect">
        <div class="fs-modal">
          <div class="fs-modal-head">
            <h3>{{ aiState.connected ? 'Edit ChatGPT connection' : 'Connect ChatGPT' }}</h3>
            <button class="fs-modal-close" @click="closeAIConnect">×</button>
          </div>
          <div class="fs-modal-body">
            <label class="fs-field">
              <span class="fs-field-label">OpenAI API Key</span>
              <input v-model="aiKeyInput" type="password"
                     class="fs-field-input"
                     placeholder="sk-..." />
              <span class="fs-field-hint">
                Get yours at
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener">platform.openai.com/api-keys</a>.
                Keys start with <code>sk-</code> and are sent to your backend — never stored in the browser.
              </span>
            </label>

            <div class="fs-info-callout">
              <strong>After connecting:</strong> The <strong>Suggest reply</strong> button in
              chat generates three reply suggestions per click (different tones) that the agent
              can pick from and edit before sending. Costs roughly $0.005–0.04 per click.
            </div>

            <div v-if="aiError" class="fs-form-error">{{ aiError }}</div>
          </div>
          <div class="fs-modal-foot">
            <button class="fs-btn-secondary" @click="closeAIConnect">Cancel</button>
            <button class="fs-btn-primary"
                    :disabled="aiSaving || !aiKeyInput.trim()"
                    @click="confirmAIConnect">
              {{ aiSaving ? 'Saving…' : aiState.connected ? 'Update' : 'Connect' }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- =================================================================
         LABELS — full CRUD page
         ================================================================= -->
    <template v-else-if="view === 'settings-labels'">
      <header class="fs-page-head">
        <div class="fs-page-titles">
          <h1 class="fs-h1">{{ isAccountAdmin ? 'Manage Labels' : 'Labels' }}</h1>
          <p v-if="isAccountAdmin" class="fs-page-desc">
            Tag chats with colored labels so you can filter, group and prioritise them.
            Created here, then assign from any chat via the
            <strong>+ Label</strong> button.
          </p>
          <p v-else class="fs-page-desc">
            Tag chats with colored labels so you can filter, group and prioritise them.
            See description on when to use them. Labels are managed by your administrator.
          </p>
        </div>
        <button v-if="isAccountAdmin" class="fs-btn-primary" @click="openLabelCreate">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5"  y1="12" x2="19" y2="12"/>
          </svg>
          Create label
        </button>
      </header>

      <div class="fs-body">
        <div class="fs-table">
          <div class="fs-table-head fs-label-grid">
            <div>Label</div>
            <div>Color</div>
            <div>Description</div>
            <div></div>
          </div>

          <div v-if="!labels.length" class="fs-empty">
            {{ isAccountAdmin
                ? 'No labels yet — click "Create label" to add your first one.'
                : 'No labels yet — your administrator hasn\'t created any.' }}
          </div>

          <div v-for="lbl in labels" :key="lbl.id" class="fs-table-row fs-label-grid">
            <!-- Name + description -->
            <div class="fs-cell-account">
              <span class="fs-label-pill"
                    :style="{
                      background: lbl.color || '#0d9488',
                      borderColor: lbl.color || '#0d9488',
                    }">
                {{ lbl.title }}
              </span>
            </div>
            <!-- Color swatch -->
            <div style="display:flex;align-items:center;gap:8px;">
              <span class="fs-color-swatch"
                    :style="{ background: lbl.color || '#0d9488' }"></span>
              <code class="fs-label-hex">{{ lbl.color || '#0d9488' }}</code>
            </div>
            <!-- Description (optional — what the label is used for) -->
            <div class="fs-label-desc-cell">{{ lbl.description || '—' }}</div>
            <!-- Actions -->
            <div v-if="isAccountAdmin" class="fs-cell-actions">
              <button class="fs-kebab" title="Edit" @click.stop="openLabelEdit(lbl)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <path d="M12 20h9"/>
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                </svg>
              </button>
              <button class="fs-kebab" title="Delete" @click.stop="deleteLabel(lbl)"
                      style="color:#b91c1c;">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== Create / Edit label modal ===== -->
      <div v-if="labelModalOpen" class="fs-modal-overlay" @click.self="closeLabelModal">
        <div class="fs-modal">
          <div class="fs-modal-head">
            <h3>{{ labelEditing ? 'Edit label' : 'Create label' }}</h3>
            <button class="fs-modal-close" @click="closeLabelModal">×</button>
          </div>
          <div class="fs-modal-body">
            <label class="fs-field">
              <span class="fs-field-label">Name</span>
              <input v-model="labelForm.title" type="text"
                     class="fs-field-input"
                     placeholder="e.g. vip, lead, follow-up, support" />
            </label>
            <p v-if="labelTitleIssue" style="margin:-4px 0 10px;font-size:12px;color:#b45309;">
              {{ labelTitleIssue }}
            </p>
            <p v-else style="margin:-4px 0 10px;font-size:11.5px;color:var(--ink-muted);">
              One word — letters, numbers, - and _ (saved lowercase).
            </p>

            <label class="fs-field">
              <span class="fs-field-label">Description (optional)</span>
              <input v-model="labelForm.description" type="text"
                     class="fs-field-input"
                     placeholder="Internal note — visible to agents only" />
            </label>

            <div class="fs-field">
              <span class="fs-field-label">Color</span>
              <div class="fs-color-grid">
                <button v-for="c in LABEL_COLORS" :key="c"
                        class="fs-color-btn"
                        :class="{ active: labelForm.color === c }"
                        :style="{ background: c }"
                        :title="c"
                        @click="labelForm.color = c"></button>
              </div>
            </div>

            <!-- Live preview -->
            <div class="fs-field">
              <span class="fs-field-label">Preview</span>
              <span class="fs-label-pill"
                    :style="{
                      background: labelForm.color,
                      borderColor: labelForm.color,
                    }">
                {{ labelForm.title || 'Label name' }}
              </span>
            </div>

            <div v-if="labelError" class="fs-form-error">{{ labelError }}</div>
          </div>
          <div class="fs-modal-foot">
            <button class="fs-btn-secondary" @click="closeLabelModal">Cancel</button>
            <button class="fs-btn-primary"
                    :disabled="labelSaving || !labelForm.title.trim()"
                    @click="saveLabel">
              {{ labelSaving ? 'Saving…' : labelEditing ? 'Save changes' : 'Create label' }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- =================================================================
         MY ACCOUNT
         ================================================================= -->
    <template v-else-if="view === 'settings-account'">
      <header class="fs-page-head">
        <div class="fs-page-titles">
          <h1 class="fs-h1">My Account</h1>
          <p class="fs-page-desc">Profile, security, plan and support.</p>
        </div>
        <button class="fs-btn-secondary fs-acc-logout-btn" @click="confirmLogout">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>
      </header>

      <div class="fs-body">
        <!-- ===== Profile card ===== -->
        <section class="fs-acc-section">
          <h2 class="fs-acc-h2">Profile</h2>
          <div class="fs-acc-card">
            <!-- Avatar row -->
            <div class="fs-acc-avatar-row">
              <div class="fs-acc-avatar-big">
                <img v-if="acctState.avatarPreview || currentUser.avatar_url"
                     :src="acctState.avatarPreview || currentUser.avatar_url"
                     :alt="currentUser.name" />
                <span v-else>{{ getInitials(currentUser.name) }}</span>
              </div>
              <div class="fs-acc-avatar-actions">
                <input ref="avatarFileRef" type="file" accept="image/*"
                       style="display:none;" @change="onAvatarFile" />
                <button class="fs-btn-secondary" @click="$refs.avatarFileRef.click()">
                  Upload new avatar
                </button>
                <button v-if="currentUser.avatar_url || acctState.avatarFile"
                        class="fs-btn-link-danger" @click="removeAvatar">
                  Remove avatar
                </button>
                <div class="fs-acc-hint">JPG/PNG, ideally square, under 5 MB.</div>
              </div>
            </div>

            <!-- Fields -->
            <label class="fs-field">
              <span class="fs-field-label">Name</span>
              <input v-model="acctForm.name" type="text" class="fs-field-input"
                     placeholder="Your full name" />
            </label>

            <label class="fs-field">
              <span class="fs-field-label">Email</span>
              <input :value="currentUser.email || ''" type="email"
                     class="fs-field-input" readonly disabled />
              <span class="fs-field-hint">
                Email changes go through a verification flow — not editable
                directly here.
              </span>
            </label>

            <div v-if="acctState.profileError" class="fs-form-error">{{ acctState.profileError }}</div>
            <div v-if="acctState.profileSavedFlash" class="fs-form-ok">✓ Profile saved.</div>

            <div class="fs-acc-card-foot">
              <button class="fs-btn-primary"
                      :disabled="acctState.profileSaving"
                      @click="saveProfile">
                {{ acctState.profileSaving ? 'Saving…' : 'Save profile' }}
              </button>
            </div>
          </div>
        </section>

        <!-- ===== Security card =====
             Collapsed by default — just a single CTA. Clicking it expands
             the three-field password form. Cancel folds it back; a
             successful save shows the green flash + recollapses. -->
        <section class="fs-acc-section">
          <h2 class="fs-acc-h2">Security</h2>
          <div class="fs-acc-card">
            <template v-if="!acctPw.open">
              <div v-if="acctPw.okFlash" class="fs-form-ok fs-form-ok--inline">
                ✓ Password updated.
              </div>
              <div class="fs-acc-pw-collapsed">
                <button class="fs-btn-primary"
                        @click="openPwForm">
                  Update password
                </button>
              </div>
            </template>

            <template v-else>
              <label class="fs-field">
                <span class="fs-field-label">Current password</span>
                <input v-model="acctPw.current" type="password" class="fs-field-input"
                       autocomplete="current-password" />
              </label>
              <label class="fs-field">
                <span class="fs-field-label">New password</span>
                <input v-model="acctPw.next" type="password" class="fs-field-input"
                       autocomplete="new-password" />
                <span class="fs-field-hint">Min. 8 characters; mix letters + numbers.</span>
              </label>
              <label class="fs-field">
                <span class="fs-field-label">Confirm new password</span>
                <input v-model="acctPw.confirm" type="password" class="fs-field-input"
                       autocomplete="new-password" />
              </label>
              <div v-if="acctPw.error" class="fs-form-error">{{ acctPw.error }}</div>
              <div class="fs-acc-card-foot fs-acc-card-foot--split">
                <button class="fs-btn-link"
                        :disabled="acctPw.saving"
                        @click="cancelPwForm">
                  Cancel
                </button>
                <button class="fs-btn-primary"
                        :disabled="acctPw.saving"
                        @click="savePassword">
                  {{ acctPw.saving ? 'Updating…' : 'Update password' }}
                </button>
              </div>
            </template>
          </div>
        </section>

        <!-- ===== Plan card ===== -->
        <section class="fs-acc-section">
          <h2 class="fs-acc-h2">Plan</h2>
          <div class="fs-acc-card fs-acc-plan-card">
            <div class="fs-acc-plan-row">
              <div>
                <div class="fs-acc-plan-label">Current plan</div>
                <div class="fs-acc-plan-value">
                  <span class="fs-acc-plan-badge">FREE</span>
                  <span class="fs-acc-plan-name">Creator-Chat Free</span>
                </div>
              </div>
              <div>
                <div class="fs-acc-plan-label">Status</div>
                <div class="fs-acc-plan-value">
                  <span class="fs-status-dot fs-status-dot--on" style="margin-right:6px;"></span>
                  Active
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ===== Support card ===== -->
        <section class="fs-acc-section">
          <h2 class="fs-acc-h2">Contact support</h2>
          <div class="fs-acc-card">
            <p class="fs-acc-support-lead">
              Need help, found a bug, or want a feature? Drop us a line —
              we read every message.
            </p>
            <div class="fs-acc-support-grid">
              <a class="fs-acc-support-link"
                 :href="`mailto:support@creator-chat.com?subject=Support%20request%20from%20${encodeURIComponent(currentUser.email || 'user')}`">
                <span class="fs-acc-support-icon">✉️</span>
                <div>
                  <div class="fs-acc-support-title">Email support</div>
                  <div class="fs-acc-support-sub">support@creator-chat.com</div>
                </div>
              </a>
            </div>
          </div>
        </section>

        <!-- ===== Danger zone — self-service account deletion ===== -->
        <section class="fs-acc-section">
          <h2 class="fs-acc-h2" style="color:#b91c1c;">Danger zone</h2>
          <div class="fs-acc-card">
            <template v-if="!deleteAcct.open">
              <p class="fs-acc-support-lead">Permanently delete your account. This cannot be undone.</p>
              <button class="fs-btn-secondary fs-btn-danger-ghost" @click="openDeleteAccount">Delete my account</button>
            </template>
            <template v-else-if="deleteAcct.done">
              <p style="margin:0 0 4px;font-weight:700;color:#0f766e;">✓ Account deleted</p>
              <p class="fs-acc-support-lead">Your account has been permanently deleted. Taking you back to the login…</p>
            </template>
            <template v-else>
              <p style="margin:0 0 4px;font-weight:700;color:#b91c1c;">{{ deleteTitle }}</p>
              <p class="fs-acc-support-lead">{{ deleteBody }}</p>
              <p style="font-size:12.5px;color:#b91c1c;font-weight:600;">This is permanent and cannot be undone.</p>
              <label class="fs-field" style="margin-top:10px;">
                <span class="fs-field-label">Enter your password to confirm</span>
                <input v-model="deleteAcct.password" type="password" autocomplete="current-password"
                       class="fs-field-input" placeholder="Your password"
                       @keydown.enter.prevent="confirmDeleteAccount" />
              </label>
              <p v-if="deleteAcct.error" style="color:#b91c1c;font-size:13px;margin:6px 0 0;">{{ deleteAcct.error }}</p>
              <div style="display:flex;gap:8px;margin-top:14px;">
                <button class="fs-btn-secondary" :disabled="deleteAcct.busy" @click="closeDeleteAccount">Cancel</button>
                <button class="fs-btn-primary" style="background:#b91c1c;border-color:#b91c1c;"
                        :disabled="deleteAcct.busy || !deleteAcct.password"
                        @click="confirmDeleteAccount">
                  {{ deleteAcct.busy ? 'Deleting…' : 'Delete permanently' }}
                </button>
              </div>
            </template>
          </div>
        </section>

        <!-- ===== Footer links ===== -->
        <div class="fs-acc-footer">
          <a class="fs-acc-footer-link" href="https://creator-chat.com/faq.html" target="_blank" rel="noopener">Visit FAQ</a>
          <span class="fs-acc-footer-sep">·</span>
          <a class="fs-acc-footer-link" href="https://www.creator-chat.com" target="_blank" rel="noopener">www.creator-chat.com</a>
        </div>
      </div>
    </template>

    <!-- ===== HELP / CONTACT — visible to admins AND agents ===== -->
    <template v-else-if="view === 'settings-help'">
      <header class="fs-page-head">
        <div class="fs-page-titles">
          <h1 class="fs-h1">Help &amp; Contact</h1>
          <p class="fs-page-desc">Reach the Creator-Chat team or browse the FAQ.</p>
        </div>
      </header>

      <div class="fs-body">
        <section class="fs-acc-section">
          <h2 class="fs-acc-h2">Contact</h2>
          <div class="fs-acc-card">
            <div class="fs-acc-support-grid">
              <a class="fs-acc-support-link"
                 :href="`mailto:info@creator-chat.com?subject=Support%20request%20from%20${encodeURIComponent(currentUser.email || 'user')}`">
                <span class="fs-acc-support-icon">✉️</span>
                <div>
                  <div class="fs-acc-support-title">Email us</div>
                  <div class="fs-acc-support-sub">info@creator-chat.com</div>
                </div>
              </a>
              <a class="fs-acc-support-link" href="https://creator-chat.com/faq.html" target="_blank" rel="noopener">
                <span class="fs-acc-support-icon">❓</span>
                <div>
                  <div class="fs-acc-support-title">Read the FAQ</div>
                  <div class="fs-acc-support-sub">Common questions &amp; setup guides</div>
                </div>
              </a>
            </div>
          </div>
        </section>
      </div>
    </template>

    <div v-else class="fs-empty">Unknown settings view.</div>

    <!-- 6-step Add-Account wizard (Telegram + Fangate vault). Teleported to
         body inside the component itself, so position here is logical
         only. Replaces the dead "coming soon" modal further up which we'll
         clean up in a follow-up. -->
    <FangateAddAccountModal
      v-model:open="addAccountModalOpen"
      :agents="agents"
      :inboxes="inboxes"
      :reconnect-inbox="reconnectInbox"
      @created="onAccountCreated"
    />
  </div>
</template>

<style lang="scss" scoped>
.fs-root {
  display: flex; flex-direction: column; height: 100%;
  background: var(--bg-main);
  position: relative;          // anchor for the mobile in-header back button
}
// Mobile back-to-settings-menu button — sits in a left gutter beside the section
// title and scrolls with the content (rendered only on mobile via v-if).
.fs-back-float {
  position: absolute; top: 15px; left: 12px; z-index: 5;
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px;
  border: none; border-radius: 9px;
  background: var(--bg-panel); color: var(--ink-strong);
  box-shadow: 0 1px 3px rgba(31, 42, 42, 0.12);
  cursor: pointer;
  transition: transform 0.08s, background 0.12s;
  &:hover { background: var(--bg-subtle); }
  &:active { transform: scale(0.9); }
}

// ---- Page head -------------------------------------------------------------
.fs-page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 28px 16px;
  border-bottom: 1px solid var(--border-soft);
  background: var(--bg-panel);
  flex-shrink: 0;
}
.fs-page-titles { flex: 1; min-width: 0; }
.fs-h1 {
  font-family: 'Libre Baskerville', serif;
  font-size: 22px; font-weight: 700;
  color: var(--ink-strong);
  letter-spacing: 0.005em;
  margin-bottom: 4px;
}
.fs-page-desc {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-muted);
  line-height: 1.5;
  max-width: 680px;
}
// In-header call-out: registering on Fangate WITH the Creator-Chat referral
// code is required for the link to work. Plain text inside the white header —
// only "Important:" and the CODE chip are tinted green.
.fs-fangate-note {
  margin: 8px 0 0;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-strong);
  max-width: 760px;
  a {
    color: var(--accent-strong);
    font-weight: 600;
    text-decoration: underline;
  }
  .fg-imp { color: var(--accent-strong); font-weight: 700; }
  .fg-code {
    background: var(--accent-tint);
    color: var(--accent-strong);
    border: 1px solid var(--accent);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
  }
}
// ---- Chat-Accounts table sort control --------------------------------------
.fs-table-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}
.fs-sort-wrap { position: relative; }
.fs-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: var(--bg-panel);
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  &:hover { border-color: var(--border-medium); }
}
.fs-sort-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 168px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  box-shadow: 0 4px 18px rgba(31, 42, 42, 0.13);
  padding: 5px;
}
.fs-sort-opt {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 9px;
  border: none;
  background: none;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-strong);
  cursor: pointer;
  text-align: left;
  &:hover { background: var(--bg-subtle); }
  &.active { color: var(--accent-strong); font-weight: 600; }
}
.fs-sort-radio {
  position: relative;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 1.5px solid var(--border-medium);
  flex-shrink: 0;
  &.checked { border-color: var(--accent); }
  &.checked::after {
    content: '';
    position: absolute;
    inset: 2.5px;
    border-radius: 50%;
    background: var(--accent);
  }
}
.fs-btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px;
  border-radius: 9px;
  border: none;
  background: var(--accent);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(13, 148, 136, 0.3);
  transition: background 0.12s;
  flex-shrink: 0;
  &:hover:not(:disabled) { background: var(--accent-strong); }
  &:disabled {
    background: var(--bg-subtle);
    color: var(--ink-muted);
    box-shadow: none;
    cursor: not-allowed;
  }
}

// ---- "Coming soon" treatment (used on the ChatGPT card while the backend
// integration is being wired) -----------------------------------------------
.fs-coming-soon-pill {
  display: inline-block;
  margin-left: 10px;
  padding: 2px 9px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: var(--bg-subtle);
  color: var(--ink-muted);
  border: 1px solid var(--border-soft);
  vertical-align: middle;
}
.fs-coming-soon-note {
  display: block;
  margin-top: 4px;
  color: var(--ink-muted);
  font-style: italic;
  font-size: 12px;
}
// Dim the provider table while the integration is unavailable, so it reads
// as "preview" rather than "broken connect button on a fully active card".
.fs-table.is-coming-soon {
  opacity: 0.55;
  pointer-events: none;          // no false hover/click affordances on cells
  filter: saturate(0.7);
}

// ---- Body / table ----------------------------------------------------------
.fs-body {
  flex: 1; min-height: 0;
  overflow-y: auto;
  padding: 22px 28px 32px;
  max-width: 1100px;
  width: 100%;
}

// ---- Mobile (≤768px) -------------------------------------------------------
// On phones the page header (long intro text) was pinned while the rows
// scrolled UNDER it, so the opaque header covered them. Let the WHOLE panel
// scroll instead — the header scrolls away with the content. Also tighten the
// generous desktop side-padding and let the header wrap the action button.
@media (max-width: 768px) {
  // Cream page background (matches the Labels list); the white header strip
  // blends into it, leaving the table/cards as the only white surfaces.
  .fs-root { overflow-y: auto; background: var(--bg-middle); }
  .fs-page-head { background: transparent; }
  .fs-body { flex: 0 0 auto; overflow-y: visible; padding: 16px 16px 28px; }
  // Stack the header on phones: title + intro text use the FULL width (≈half as
  // tall as the old narrow column), and the action button drops below them
  // (title + button don't fit on one line at phone width).
  .fs-page-head { flex-direction: column; align-items: stretch; gap: 12px; padding: 16px 16px 14px 56px; }   // left gutter for the back button
  .fs-page-head .fs-btn-primary { align-self: flex-start; }

  // Chat-Accounts table → stacked cards (the 6-col grid is far too wide for a
  // phone, so columns got squished/cut off → "unvollständig"). Hide the column
  // header; each row becomes a card: account name as the title, kebab top-right,
  // and labelled detail rows for Type / Agents / Fangate so ALL info shows.
  // (.fs-root prefix lifts specificity so these beat the base grid rules.)
  .fs-root .fs-table-head { display: none; }
  .fs-root .fs-table-row {
    position: relative;
    display: flex; flex-direction: column; align-items: stretch;
    gap: 9px; padding: 14px 44px 14px 16px;
  }
  .fs-root .fs-cell-actions { position: absolute; top: 10px; right: 8px; }
  .fs-root .fs-cell-account { order: -1; }
  .fs-root .fs-cell-account .fs-acc-name-text { font-size: 15px; font-weight: 700; }
  .fs-root .fs-cell-type,
  .fs-root .fs-cell-agents,
  .fs-root .fs-cell-fangate {
    display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
  }
  .fs-root .fs-cell-type::before,
  .fs-root .fs-cell-agents::before,
  .fs-root .fs-cell-webhook::before,
  .fs-root .fs-cell-fangate::before {
    flex: 0 0 64px;
    font-family: 'Inter', sans-serif; font-size: 9px; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-muted);
  }
  .fs-root .fs-cell-type::before { content: 'Type'; }
  .fs-root .fs-cell-agents::before { content: 'Agents'; }
  .fs-root .fs-cell-webhook::before { content: 'Webhook'; }
  .fs-root .fs-cell-fangate::before { content: 'Fangate'; }
}
.fs-table {
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  overflow: visible; // allow kebab menu to escape
}
.fs-table-head,
.fs-table-row {
  display: grid;
  // 6 columns: status-stack | account-name | type | agents | fangate | actions
  // The status stack has a fixed width so the ACCOUNT header column aligns
  // with the name across rows whether or not there's a status pill rendered.
  grid-template-columns: 140px 1.6fr 170px 1.2fr 0.85fr 1fr 42px;
  gap: 14px;
  padding: 10px 18px;
  border-bottom: 1px solid var(--border-soft);
  align-items: center;
}
// Grid cells must be able to shrink below their content's intrinsic width.
// Otherwise a long value (e.g. a linked Fangate email) widens its fr column,
// which steals space from the other fr columns and throws the row out of
// alignment with the header (the bug: row askew from the TYPE column on).
.fs-table-head > *,
.fs-table-row > * { min-width: 0; }
.fs-cell-fangate { overflow: hidden; }
.fs-cell-fangate .fs-pill { max-width: 100%; }
.fs-link-dot { flex: none; }
.fs-link-email { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.fs-table-row:last-child { border-bottom: none; }
.fs-table-row.is-inactive { opacity: 0.5; }
.fs-table-head {
  background: var(--bg-subtle);
  font-family: 'Inter', sans-serif;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-strong);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}
.fs-empty {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: var(--ink-muted);
  font-style: italic;
  padding: 28px 18px;
  text-align: center;
}

// ---- Status cell (column 1) -----------------------------------------------
// Status pill (top) + optional recovery action link pill (bottom). Stacked
// vertically. Empty for Bot/Personal inboxes (they have no health concept).
.fs-cell-status {
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
  min-width: 0;
}
.fs-acc-action-pill {
  background: rgba(255, 255, 255, 0.55);
  color: inherit;
  text-decoration: none;
  font-size: 10px;
  letter-spacing: 0.02em;
  &:hover { background: rgba(255, 255, 255, 0.95); }
}

// ---- Account cell (column 2) ----------------------------------------------
.fs-cell-account {
  min-width: 0;
}
.fs-acc-platform-icon {
  flex-shrink: 0;
  display: block;
}
.fs-acc-name-text {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fs-acc-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 13px; font-weight: 600;
  color: var(--ink-strong);
  display: flex; align-items: center; gap: 8px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fs-acc-handle {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 1px;
}

// ---- Pills ------------------------------------------------------------------
.fs-pill {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 8px;
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 600;
  background: var(--bg-subtle); color: var(--ink-muted);
  &--user, &--agent {
    background: var(--accent-tint);
    color: var(--accent-strong);
  }
  &--bot {
    background: #fef3c7;
    color: #92400e;
  }
  &--inactive {
    background: #fee2e2;
    color: #991b1b;
    text-transform: uppercase;
    font-size: 9px;
    letter-spacing: 0.04em;
  }
  // Phase C-3 health pill — colour communicates severity, the banner above the
  // table carries the actionable detail. Hover title surfaces the full text.
  &--green  { background: rgba(16, 185, 129, 0.16); color: #047857; }
  &--red    { background: #fee2e2; color: #991b1b; }
  &--orange { background: #ffedd5; color: #9a3412; }
  &--yellow { background: #fef3c7; color: #92400e; }
  &--linked {
    background: rgba(16, 185, 129, 0.16);
    color: #047857;
  }
  &--rejected {
    background: #fee2e2;
    color: #b91c1c;
  }
}
.fs-health-pill {
  // tighter letter spacing than the inactive pill — labels can be longer
  // (e.g. "Paused (1h 28m)").
  letter-spacing: 0.02em;
}

// Per-inbox health banner, stacked above the table.
.fs-health-banner {
  display: flex; align-items: center; gap: 16px;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 10px;
  font-family: 'Inter', sans-serif; font-size: 13px;
  border: 1px solid transparent;
  &--critical { background: #fee2e2; border-color: #fecaca; color: #7f1d1d; }
  &--high     { background: #ffedd5; border-color: #fed7aa; color: #7c2d12; }
  &--medium   { background: #fef3c7; border-color: #fde68a; color: #78350f; }
  &--low      { background: #f1f5f9; border-color: #e2e8f0; color: #334155; }
}
.fs-health-banner-body {
  display: flex; flex-direction: column; gap: 2px;
  flex: 1 1 auto; min-width: 0;
}
.fs-health-banner-name { font-weight: 700; font-size: 12.5px; }
.fs-health-banner-text { font-size: 12.5px; line-height: 1.4; }
.fs-health-banner-cta {
  flex-shrink: 0;
  padding: 7px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.65);
  color: inherit;
  font-weight: 700; font-size: 12px;
  text-decoration: none;
  white-space: nowrap;
  &:hover { background: rgba(255, 255, 255, 0.95); }
}
.fs-link-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
}
.fs-cell-agents {
  display: flex; flex-wrap: wrap; gap: 3px;
  cursor: pointer;
  padding: 4px 0;
  border-radius: 6px;
  transition: background 0.12s;
  &:hover { background: var(--bg-subtle); }
}
.fs-muted-italic {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  font-style: italic;
}

// ---- Kebab menu ------------------------------------------------------------
.fs-cell-actions {
  display: flex; justify-content: flex-end;
  position: relative;
}
.fs-kebab {
  display: inline-flex !important;
  align-items: center; justify-content: center;
  width: 28px; height: 28px;
  padding: 0 !important;
  border-radius: 7px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink-muted);
  cursor: pointer;
  transition: all 0.1s;
  &:hover {
    background: var(--bg-subtle);
    border-color: var(--border-soft);
    color: var(--ink-strong);
  }
}
.fs-kebab-menu {
  position: absolute;
  top: 32px; right: 0;
  min-width: 180px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(11, 20, 20, 0.14);
  padding: 5px;
  z-index: 200;
}
.fs-menu-item {
  display: flex; align-items: center; gap: 9px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 7px;
  border: none;
  background: transparent;
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 500;
  cursor: pointer;
  text-align: left;
  &:hover { background: var(--bg-subtle); }
  &.is-warn { color: #d97706; }
  &.is-ok   { color: #059669; }
  &.is-danger { color: #b91c1c; }
  &.is-danger:hover { background: #fef2f2; }
}
.fs-menu-sep {
  height: 1px; background: var(--border-soft); margin: 4px 4px;
}

// ---- Help section ----------------------------------------------------------
.fs-help {
  margin-top: 24px;
  border-radius: 10px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  overflow: hidden;
}
.fs-help-summary {
  cursor: pointer;
  padding: 14px 18px;
  display: flex; align-items: center; gap: 10px;
  list-style: none;
  user-select: none;
  transition: background 0.12s;
  font-family: 'Inter', sans-serif;
  &::-webkit-details-marker { display: none; }
  &:hover { background: var(--bg-subtle); }
  span {
    font-size: 13.5px; font-weight: 600;
    color: var(--ink-strong);
    flex: 1;
  }
}
.fs-help-chev {
  color: var(--ink-muted);
  transition: transform 0.2s;
  flex-shrink: 0;
}
.fs-help[open] .fs-help-chev { transform: rotate(180deg); }
.fs-help-body {
  padding: 4px 18px 18px;
  border-top: 1px solid var(--border-soft);
}
.fs-help-section { margin-top: 14px; }
.fs-help-platform {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700;
  color: var(--ink-strong);
}
.fs-help-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 700;
  color: var(--ink-medium);
  margin-bottom: 4px;
  margin-top: 10px;
  &:first-of-type { margin-top: 0; }
}
// Intro line at the top of a collapsible's body — sets the scene before the
// per-platform bullet sections kick in.
.fs-help-intro {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-medium);
  line-height: 1.6;
  margin: 0 0 14px;
}
.fs-help-list {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-medium);
  line-height: 1.7;
  padding-left: 20px;
  margin: 0;
  li { margin: 0; }
  a {
    color: var(--accent-strong);
    text-decoration: underline;
    &.fg-link { color: #5b2ecf; font-weight: 600; }
  }
  code {
    background: var(--bg-subtle);
    padding: 1px 5px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11.5px;
  }
  strong { color: var(--ink-strong); }
}
.fs-help-divider {
  height: 1px;
  background: var(--border-soft);
  margin: 18px 0;
}

// ---- Bots / Account card (unchanged) ---------------------------------------
.fs-card {
  display: flex; flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 36px 24px;
  border-radius: 14px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  text-align: center;
  max-width: 480px;
  margin: 24px auto 0;
}
.fs-card-icon { color: var(--accent-strong); opacity: 0.6; }
.fs-card-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 15px; font-weight: 700;
  color: var(--ink-strong);
}
.fs-card-sub {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; line-height: 1.55;
  color: var(--ink-muted);
}
.fs-profile-card {
  max-width: 460px;
  margin: 12px auto 0;
  padding: 28px 28px 22px;
  border-radius: 14px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  text-align: center;
}
.fs-profile-avatar {
  width: 80px; height: 80px;
  border-radius: 50%;
  margin: 0 auto 12px;
  background: var(--accent-tint);
  border: 2px solid var(--accent-ring);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Libre Baskerville', serif;
  font-size: 28px; font-weight: 700;
  color: var(--accent-strong);
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fs-profile-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 17px; font-weight: 700;
  color: var(--ink-strong);
}
.fs-profile-role {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent-strong);
  margin-top: 4px;
}
.fs-profile-list {
  margin-top: 20px;
  display: flex; flex-direction: column;
  text-align: left;
}
.fs-profile-row {
  display: flex; justify-content: space-between; align-items: baseline;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-soft);
  &:last-child { border-bottom: none; }
  dt {
    font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 600;
    color: var(--ink-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  dd {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: var(--ink-strong);
    margin: 0;
    text-align: right;
    word-break: break-all;
  }
}

// ============================================================================
// My Account page — full profile/security/plan/support layout (Task #209)
// ============================================================================
.fs-acc-logout-btn {
  flex-shrink: 0;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #fef2f2;
    border-color: #fecaca;
    color: #b91c1c;
  }
}
.fs-acc-section {
  max-width: 720px;
  margin-bottom: 28px;
}
// Footer links at the bottom of My Account (FAQ + marketing site), both teal.
.fs-acc-footer {
  max-width: 720px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 6px 0 30px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
}
.fs-acc-footer-link {
  color: var(--accent-strong);
  font-weight: 600;
  text-decoration: none;
  &:hover { color: var(--accent); text-decoration: underline; }
}
.fs-acc-footer-sep { color: var(--ink-muted); }
.fs-acc-h2 {
  font-family: 'Libre Baskerville', serif;
  font-size: 15px; font-weight: 700;
  color: var(--ink-strong);
  margin: 0 0 10px;
  letter-spacing: 0.01em;
}
.fs-acc-card {
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 20px 22px;
}
.fs-acc-avatar-row {
  display: flex; align-items: center; gap: 18px;
  margin-bottom: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--border-soft);
}
.fs-acc-avatar-big {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-size: 26px; font-weight: 700;
  font-family: 'Inter', sans-serif;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fs-acc-avatar-actions {
  display: flex; flex-direction: column; gap: 6px;
  align-items: flex-start;
}
.fs-acc-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  line-height: 1.4;
}
.fs-acc-card-foot {
  display: flex; justify-content: flex-end;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-soft);
}
// Variant: Cancel on the left, primary CTA on the right.
.fs-acc-card-foot--split { justify-content: space-between; align-items: center; }
.fs-btn-link-danger {
  border: none; background: transparent;
  color: #b91c1c;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
  &:hover { text-decoration: underline; }
}
.fs-btn-link {
  border: none; background: transparent;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 12px; font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
  &:hover { color: var(--ink-base); text-decoration: underline; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
// Security card — collapsed-state container. No top border, no padding-top,
// just the CTA sitting flush with the card padding.
.fs-acc-pw-collapsed {
  display: flex; justify-content: flex-end;
}
// fs-form-ok variant for the collapsed Security state where there's no
// other content above it — drop the top margin so the green flash sits
// at the top of the card cleanly.
.fs-form-ok--inline { margin-top: 0; margin-bottom: 12px; }
.fs-form-error {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}
.fs-form-ok {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
}

// Plan card
.fs-acc-plan-card { padding: 18px 22px 14px; }
.fs-acc-plan-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 10px;
}
.fs-acc-plan-label {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-muted);
  margin-bottom: 4px;
}
.fs-acc-plan-value {
  display: flex; align-items: center; gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 600;
  color: var(--ink-strong);
}
.fs-acc-plan-badge {
  display: inline-flex; align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 800;
  letter-spacing: 0.08em;
}
.fs-acc-plan-name {
  font-size: 13.5px;
  color: var(--ink-strong);
}
.fs-acc-plan-note {
  margin: 8px 0 0;
  padding-top: 12px;
  border-top: 1px solid var(--border-soft);
  font-family: 'Inter', sans-serif;
  font-size: 12px; line-height: 1.5;
  color: var(--ink-muted);
}

// Support card
.fs-acc-support-lead {
  margin: 0 0 14px;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; line-height: 1.55;
  color: var(--ink-medium);
}
.fs-acc-support-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.fs-acc-support-link {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  text-decoration: none;
  color: var(--ink-strong);
  transition: border-color 0.12s, background 0.12s;
  &:hover {
    border-color: var(--accent);
    background: var(--accent-tint);
  }
}
.fs-acc-support-icon {
  font-size: 22px;
  line-height: 1;
}
.fs-acc-support-title {
  font-family: 'Inter', sans-serif;
  font-size: 13px; font-weight: 700;
  color: var(--ink-strong);
}
.fs-acc-support-sub {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-muted);
  margin-top: 2px;
}

// ============================================================================
// Modal overlay (Add / Edit account)
// ============================================================================
.fs-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(11, 20, 20, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}
.fs-modal {
  width: 100%;
  max-width: 520px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(11, 20, 20, 0.30);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}
.fs-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-soft);
  h3 {
    font-family: 'Libre Baskerville', serif;
    font-size: 16px; font-weight: 700;
    color: var(--ink-strong);
  }
}
.fs-modal-close {
  border: none; background: transparent;
  width: 28px; height: 28px;
  border-radius: 6px;
  padding: 0 !important;
  font-size: 18px;
  color: var(--ink-muted);
  cursor: pointer;
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}
.fs-modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}
.fs-modal-icon {
  width: 64px; height: 64px;
  margin: 0 auto 14px;
  border-radius: 50%;
  background: var(--accent-tint);
  border: 1.5px solid var(--accent-ring);
  display: flex; align-items: center; justify-content: center;
  color: var(--accent-strong);
}
.fs-modal-title {
  text-align: center;
  font-family: 'Libre Baskerville', serif;
  font-size: 15px; font-weight: 700;
  color: var(--ink-strong);
  margin-bottom: 6px;
}
.fs-modal-text {
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-muted);
  line-height: 1.55;
  max-width: 400px;
  margin: 0 auto;
}
.fs-modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px;
  border-top: 1px solid var(--border-soft);
  background: var(--bg-subtle);
}
.fs-modal-foot--split {
  justify-content: space-between;
}

// Form fields inside the edit modal
.fs-form-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  background: var(--bg-subtle);
  margin-bottom: 14px;
}
.fs-form-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  background: var(--accent-tint);
  border: 1.5px solid var(--accent-ring);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Libre Baskerville', serif;
  font-size: 16px; font-weight: 700;
  color: var(--accent-strong);
  overflow: hidden; flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.fs-form-acc-info { min-width: 0; }
.fs-form-acc-platform {
  font-family: 'Inter', sans-serif;
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent-strong);
}
.fs-form-acc-handle {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  margin-top: 2px;
}
.fs-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 14px;
}
.fs-field-label {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 600;
  color: var(--ink-medium);
  letter-spacing: 0.02em;
}
.fs-field-input {
  padding: 9px 12px;
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  background: var(--bg-main);
  color: var(--ink-strong);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
  &:focus {
    border-color: var(--accent-ring);
    box-shadow: 0 0 0 2px var(--accent-tint);
  }
  &::placeholder { color: var(--ink-muted); }
}
.fs-field-textarea {
  resize: vertical;
  min-height: 70px;
  font-family: 'Inter', sans-serif;
  line-height: 1.45;
}

// Read-only display for fields the user can't change in this dialog (e.g. the
// account type of a Channel::TelegramUser inbox — fixed by the channel class).
.fs-field-readonly {
  background: var(--bg-subtle);
  color: var(--ink-muted);
  cursor: default;
  display: flex; flex-direction: column; gap: 4px;
}
.fs-field-hint {
  font-size: 11px;
  font-weight: 500;
  color: var(--ink-muted);
  line-height: 1.4;
}
.fs-field-toggle {
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  input { margin-top: 3px; cursor: pointer; }
}
.fs-toggle-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
  strong {
    font-family: 'Inter', sans-serif;
    font-size: 13px; font-weight: 600;
    color: var(--ink-strong);
  }
  small {
    font-family: 'Inter', sans-serif;
    font-size: 11px;
    color: var(--ink-muted);
  }
}
.fs-form-error {
  padding: 8px 12px;
  border-radius: 8px;
  background: #fee2e2;
  color: #991b1b;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  margin-top: 8px;
}

// ----- Fangate vault section (inside edit-inbox modal) -----
.fs-fangate-section {
  margin-top: 6px;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid rgba(91, 46, 207, 0.18);
  background: rgba(91, 46, 207, 0.05);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fs-fangate-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.fs-fangate-title {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #4c1d95;
}
.fs-fangate-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.14);
  color: #047857;
  font-family: 'Inter', sans-serif;
  font-size: 10.5px;
  font-weight: 700;
  &--off {
    background: var(--bg-subtle);
    color: var(--ink-muted);
  }
}
.fs-fangate-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
}
.fs-fangate-desc {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--ink-medium);
  a { color: #5b21b6; text-decoration: none; &:hover { text-decoration: underline; } }
}
.fs-fangate-hint {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  line-height: 1.5;
  color: var(--ink-medium);
  strong { color: #4c1d95; font-weight: 700; }
}
.fs-webhook-block {
  margin-top: 2px;
  padding-top: 12px;
  border-top: 1px dashed rgba(91, 46, 207, 0.20);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.fs-webhook-head {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #4c1d95;
}
.fs-webhook-row {
  display: flex;
  align-items: center;
  gap: 6px;
  .fs-field-input { flex: 1 1 auto; min-width: 0; }
}
.fs-webhook-state {
  font-weight: 600;
  color: var(--ink-muted);
}
.fs-fangate-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.fs-btn-danger-ghost {
  color: #b91c1c;
  border-color: rgba(185, 28, 28, 0.3);
  background: transparent;
  &:hover { background: #fef2f2; border-color: #b91c1c; }
}

// Labels page ----------------------------------------------------------------
.fs-label-grid {
  grid-template-columns: 1.1fr 0.9fr 1.6fr 80px !important;
}
.fs-label-desc-cell {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fs-label-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 11px;
  border-radius: 999px;
  background: var(--accent);
  border: 1px solid var(--accent);
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
}
.fs-label-desc {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 4px;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fs-color-swatch {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  flex-shrink: 0;
  border: 1px solid var(--border-soft);
}
.fs-label-hex {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--ink-muted);
}
.fs-color-grid {
  display: grid;
  grid-template-columns: repeat(6, 32px);
  gap: 6px;
  margin-top: 4px;
}
.fs-color-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0 !important;
  transition: transform 0.12s, border-color 0.12s, box-shadow 0.12s;
  &:hover { transform: scale(1.08); }
  &.active {
    border-color: var(--ink-strong);
    box-shadow: 0 0 0 2px var(--bg-panel),
                0 0 0 4px var(--ink-strong);
  }
}

// Bots & AI specifics ---------------------------------------------------------
.fs-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin: 38px 0 18px;
}
.fs-h2 {
  font-family: 'Libre Baskerville', serif;
  font-size: 18px; font-weight: 700;
  color: var(--ink-strong);
  margin-bottom: 4px;
}
// Bot row grid (6 cols: Bot · Mode · Keywords · Actions · Status · ⋯ —
// "Actions" is the action-type overview column; the trailing 42px is the
// edit/delete kebabs. Keywords + Actions get the most slack since both
// hold variable-width chip lists.)
.fs-bot-grid {
  grid-template-columns: 1.35fr 0.85fr 1.1fr 1.3fr 0.7fr 42px !important;
}
// Action-type chip column: a compact pill per action type, with icon + label
// wrapped together. Same wrap behavior as the Keywords column so a bot with
// many actions stays readable instead of overflowing horizontally.
.fs-bot-actions-col {
  display: flex; flex-wrap: wrap; gap: 4px;
  align-items: center;
}
.fs-bot-action-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px 2px 6px;
  border-radius: 999px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 600;
  white-space: nowrap;
}
.fs-bot-action-chip-icon { font-size: 11px; line-height: 1; }
.fs-bot-action-chip-label {
  // Hide label on very narrow viewports so just the icon shows + the tooltip
  // (title attr) still carries the full label.
  @media (max-width: 900px) { display: none; }
}
// AI row grid (5 columns)
.fs-ai-grid {
  grid-template-columns: 1.8fr 1.4fr 0.9fr 80px !important;
}
.fs-bot-icon {
  width: 34px; height: 34px;
  border-radius: 8px;
  background: var(--accent-tint);
  color: var(--accent-strong);
  display: flex; align-items: center; justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}
.fs-openai-icon {
  width: 34px; height: 34px;
  border-radius: 8px;
  background: #10a37f;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
// Status pills
.fs-status-dot {
  width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
  &--on { background: #10b981; }
  &--off { background: #cbc7b8; }
  &--paused { background: #f59e0b; }
}
.fs-pill--status-on {
  background: #d1fae5 !important;
  color: #065f46 !important;
}
.fs-pill--status-off {
  background: var(--bg-subtle) !important;
  color: var(--ink-muted) !important;
}
.fs-pill--status-paused {
  background: #fef3c7 !important;
  color: #92400e !important;
}

// Bot builder modal — chooser cards + builder form + keyword pills ----------
.fs-bot-modal {
  // Wider than the default modal so the form fields breathe.
  max-width: 540px !important;
}
.fs-bot-choice-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.fs-bot-choice {
  display: flex; flex-direction: column; align-items: flex-start;
  gap: 8px;
  padding: 18px 16px;
  background: var(--bg-panel);
  border: 1.5px solid var(--border-soft);
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
  &:hover:not(:disabled) {
    border-color: var(--accent);
    background: var(--accent-tint);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}
.fs-bot-choice-emoji {
  font-size: 28px;
  line-height: 1;
}
.fs-bot-choice-title {
  font-size: 14px; font-weight: 700;
  color: var(--ink-strong);
}
.fs-bot-choice-sub {
  font-size: 12px;
  color: var(--ink-muted);
  line-height: 1.4;
}
.fs-bot-pick-list {
  display: flex; flex-direction: column; gap: 6px;
}
.fs-bot-pick-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px;
  background: var(--bg-panel);
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  &:hover {
    border-color: var(--accent);
    background: var(--accent-tint);
  }
}
.fs-bot-back {
  margin-top: 10px;
  padding: 6px 10px;
  background: transparent;
  border: none;
  color: var(--ink-muted);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  cursor: pointer;
  &:hover { color: var(--ink-strong); }
}
.fs-bot-kw-row {
  display: flex; gap: 6px;
  input { flex: 1; }
}
.fs-bot-kw-pills {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin-top: 8px;
}
.fs-bot-kw-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 4px 3px 10px;
  background: var(--accent-tint);
  color: var(--accent-strong);
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; font-weight: 600;
}
.fs-bot-kw-x {
  width: 18px; height: 18px;
  border: none;
  background: transparent;
  color: var(--accent-strong);
  font-size: 14px; line-height: 1;
  cursor: pointer;
  border-radius: 50%;
  &:hover { background: rgba(13, 148, 136, 0.15); }
}

// Action editor inside the bot builder ---------------------------------------
.fs-bot-actions-list {
  display: flex; flex-direction: column; gap: 8px;
  margin-top: 6px;
}
.fs-bot-action {
  border: 1px solid var(--border-soft);
  border-radius: 10px;
  background: var(--bg-panel);
  overflow: hidden;
}
.fs-bot-action-head {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px;
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-soft);
}
.fs-bot-action-idx {
  width: 22px; height: 22px;
  border-radius: 50%;
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.fs-bot-action-type {
  flex: 1; min-width: 0;
}
.fs-bot-action-move,
.fs-bot-action-x {
  width: 26px; height: 26px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px; line-height: 1;
  color: var(--ink-medium);
  &:hover:not(:disabled) { border-color: var(--accent); color: var(--accent-strong); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
}
.fs-bot-action-x {
  color: #b91c1c;
  font-size: 16px;
  &:hover { border-color: #b91c1c; background: #fef2f2; }
}
.fs-bot-action-body {
  padding: 10px;
  display: flex; flex-direction: column; gap: 8px;
}
.fs-bot-action-desc {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px; line-height: 1.45;
  color: var(--ink-muted);
}
.fs-bot-action-labels {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.fs-bot-action-lbl {
  padding: 3px 10px;
  border-radius: 999px;
  border: 1.5px solid;
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 600;
  cursor: pointer;
  background: transparent;
  transition: transform 0.08s;
  &:hover { transform: scale(1.03); }
}
.fs-bot-add-action {
  display: flex; gap: 8px;
  margin-top: 10px;
  select { flex: 1; }
}
// Invite-link action: composite form layout. Stacks the 4 inputs (chat_id,
// member-limit + expiry side-by-side, template) with consistent labels and
// small grey hints under each control.
.fs-bot-invite-grid {
  display: flex; flex-direction: column; gap: 8px;
}
.fs-bot-invite-field {
  display: flex; flex-direction: column; gap: 3px;
  > span {
    font-family: 'Inter', sans-serif;
    font-size: 11px; font-weight: 700;
    color: var(--ink-strong);
    letter-spacing: 0.02em;
  }
  > small {
    font-family: 'Inter', sans-serif;
    font-size: 10.5px;
    color: var(--ink-muted);
    line-height: 1.35;
    code {
      background: var(--bg-subtle);
      padding: 0 4px;
      border-radius: 4px;
      font-size: 10px;
    }
  }
}
.fs-muted-italic {
  font-family: 'Inter', sans-serif;
  color: var(--ink-muted);
  font-style: italic;
}

// Bot list: per-inbox group headers — one section per Telegram account so
// the user sees at a glance "Sven's bots", "Henning's bots", etc.
.fs-bot-group-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 14px;
  margin-top: 10px;
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-soft);
  &:first-of-type { margin-top: 0; }
}
.fs-bot-group-name {
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--ink-strong);
}
.fs-bot-group-count {
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 600;
  color: var(--ink-muted);
}

// Expandable account row — the new parent in the bots table. Click anywhere
// on the row toggles expansion. Looks like a section header but is clearly
// interactive (chevron + hover background).
.fs-bot-account-row {
  display: flex; align-items: center; gap: 10px;
  padding: 11px 14px;
  margin-top: 10px;
  background: var(--bg-subtle);
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background 0.12s, border-color 0.12s;
  &:first-of-type { margin-top: 0; }
  &:hover {
    background: var(--accent-tint);
    border-color: var(--accent-ring);
  }
  &.is-collapsed {
    background: var(--bg-panel);
  }
}
.fs-bot-account-chev {
  display: inline-block;
  font-size: 10px;
  color: var(--accent-strong);
  transform: rotate(0deg);
  transition: transform 0.15s ease;
  &.is-open { transform: rotate(90deg); }
}
.fs-bot-account-name {
  font-family: 'Libre Baskerville', serif;
  font-size: 13.5px; font-weight: 700;
  color: var(--ink-strong);
  flex: 1;
}
.fs-bot-account-count {
  font-family: 'Inter', sans-serif;
  font-size: 11px; font-weight: 600;
  color: var(--ink-muted);
}

// Child bot rows: indented under their account parent so the hierarchy is
// visible at a glance. No top-border on the first child to avoid stacking
// with the account row's bottom edge.
.fs-bot-child-row {
  padding-left: 36px !important;
  background: var(--bg-panel);
  border-left: 2px solid var(--accent-ring);
  margin-left: 8px;
}

// Keyword chips in the Keywords column. Compact accent-tinted pills that
// wrap naturally so a bot with many triggers stays readable.
.fs-bot-keywords {
  display: flex; flex-wrap: wrap; gap: 4px;
  align-items: center;
}
.fs-bot-kw-chip {
  display: inline-flex; align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--accent-tint);
  color: var(--accent-strong);
  font-family: 'Inter', sans-serif;
  font-size: 10.5px; font-weight: 600;
}

// Help blocks — paragraph + lists
.fs-help-p {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-medium);
  line-height: 1.6;
  margin-top: 12px;
  &:first-child { margin-top: 4px; }
  strong { color: var(--ink-strong); }
  code {
    background: var(--bg-subtle);
    padding: 1px 5px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
  }
}
.fs-help-ul, .fs-help-ol {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: var(--ink-medium);
  line-height: 1.7;
  padding-left: 22px;
  margin: 6px 0 0;
  li { margin: 0; }
  strong { color: var(--ink-strong); }
  code {
    background: var(--bg-subtle);
    padding: 1px 5px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
  }
  a {
    color: var(--accent-strong);
    text-decoration: underline;
  }
}

// Hint text under form labels (modal)
.fs-field-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 5px;
  line-height: 1.5;
  a { color: var(--accent-strong); text-decoration: underline; }
  code {
    background: var(--bg-subtle);
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 10.5px;
    font-family: 'JetBrains Mono', monospace;
  }
}
.fs-info-callout {
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(13,148,136,0.06);
  border-left: 3px solid var(--accent);
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: var(--ink-medium);
  line-height: 1.5;
  margin-top: 4px;
  strong { color: var(--ink-strong); }
}

// Buttons (modal footer)
.fs-btn-secondary {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  background: var(--bg-panel);
  color: var(--ink-medium);
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  &:hover { background: var(--bg-subtle); color: var(--ink-strong); }
}
.fs-btn-danger {
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  &:hover { background: #fee2e2; }
}
</style>
